/**
 * StaffTechnicalPanel — staff hybrid-fill technical intake (Step-2 Slice 2).
 *
 * Lives on ProjectDetail (the staff work surface). Captures the fields the
 * CLIENT never fills:
 *   · North bearing  → clients/{id}.profile.north_rotation (Break 1 fix)
 *   · Floor scaffold + per-floor entrance codes → answers.floors[]
 *   · Site location (owner-locked convenience) + open-in-maps quick buttons
 *
 * All writes are merge — never clobbers Slice-1 client-owned answers. The
 * panel is a no-op when the project has no linked client (it tells the staff
 * to link one first).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Box, Btn3, Card, EmptyNote, Tag } from './ui';

/**
 * Workspace marking wrapper (mockup v3.5): in the dedicated evaluation workspace
 * the "Floors & grid" Box chrome (title bar + intro + add-floor) is dropped so
 * the floor-plan canvas + its marking toolbar read as the bare hero. Module-level
 * so its identity is stable (no remount of the marking surface). Accepts the same
 * title/note props as Box but ignores them.
 */
function MarkingWrap({ children }: { title: string; note?: string; children: ReactNode }) {
  return <div className="evalws-marking">{children}</div>;
}
import {
  blankFloor,
  blankStaffForm,
  blankProperty,
  normaliseNorth,
  bearingToRotation,
  loadPropertiesIntake,
  saveNorthRotation,
  saveNorthScreenshot,
  saveProperties,
  saveSiteLocation,
  saveReportContext,
  blankReportContext,
  cleanFloorBoundary,
  VALID_BEDROOM_ZONES,
  normaliseVerdictStatus,
  type StaffIntakeForm,
  type FloorInput,
  type PropertyInput,
  type ReportContextForm,
  type RoomOverrideRow,
  type VerdictStatus,
} from '../lib/staffIntake';
import { draftPropertyRecommendation } from '../lib/verdictDraft';
import {
  generatePropertyRecommendation,
  toVerdictStatus,
} from '../lib/generatePropertyRecommendation';
import {
  candidateGridStatus,
  gridBadge,
  floorHasBoundary,
  floorHasEntrance,
  floorAuoResolved,
  type GridStatus,
} from '../lib/candidateGrid';
// "Push to registry" UI removed (2026-06-28, Gap #6). Property IS the registry;
// report immutability is via versioned PDFs (L1A_v{n}) — no manual push needed.
// The lib (candidateRegistryPush.ts) is retained but no longer wired to the UI.
import {
  autoJoinMayPersist,
  isCandidateLinkVisible,
  joinCandidatesToLinkedProperties,
  type PropertyRefLike,
} from '../lib/candidatePropertyJoin';
import { getProperty } from '../lib/propertyStore';
// FIX A (owner bug 2026-07-03) — removing a stamped candidate also drops its
// registry link so Lane B (projects.propertyRefs[]) stays consistent.
import { unlinkPropertyFromProject } from '../lib/projectStore';
import {
  resolvePropertyGridForMarking,
  type PropertyGridForMarking,
} from '../lib/propertyGridForMarking';
import FloorNameCombobox from './FloorNameCombobox';
import {
  buildSiteLocation,
  hasValidCoords,
  isShortGoogleLink,
  parseGoogleMapsUrl,
  parseCoordPair,
  formatCoordPair,
  mapLaunchUrls,
} from '../lib/siteLocation';
import {
  uploadFloorPlan,
  uploadNorthScreenshot,
  uploadDerivedFloorPlan,
  uploadDerivedNorthScreenshot,
  floorPlanFromProjectFile,
  isPdfFloorPlanType,
  type FloorPlan,
  type FloorPlanSource,
  type NorthScreenshot,
} from '../lib/floorPlan';
import CropRotateEditor from './CropRotateEditor';
import PdfPagePicker from './PdfPagePicker';
import {
  type EditRecipe,
  type EditableSource,
  bakeImage,
  loadEditableSource,
  getPdfPageCount,
  derivedFileName,
  sourceRefFor,
  recipeFor,
  editDocBody,
  type OriginalImageRef,
} from '../lib/imageCropRotate';
import {
  computeGrid,
  isValidBoundary,
  type Pt,
  type GridResult,
} from '../lib/vastuGrid';
import {
  addMark,
  addInternalMark,
  moveMark,
  moveInternalMark,
  setMarkEnd,
  setInternalEnd,
  removeMark,
  setMarkType,
  setMarkName,
  setPrincipal,
  rederiveMarks,
  marksFromFloor,
  marksToFloorPatch,
  type EntranceMark,
} from '../lib/entranceMarks';
import {
  addMark as addAuo,
  moveMark as moveAuo,
  resizeMark as resizeAuo,
  resizeMarkAnchored as resizeAuoAnchored,
  rotateMark as rotateAuo,
  setMarkShape as setAuoShape,
  removeMark as removeAuo,
  setMarkName as setAuoName,
  setMarkType as setAuoType,
  setMarkZones as setAuoZones,
  applySuggestion as applyAuoSuggestion,
  applySuggestions as applyAuoSuggestions,
  confirmMark as confirmAuo,
  rejectMark as rejectAuo,
  reopenMark as reopenAuo,
  rederiveMarks as rederiveAuoMarks,
  marksFromFloor as auoMarksFromFloor,
  marksToFixtures,
  type AuoMark,
  type AuoSuggestion,
} from '../lib/auoMarks';
import {
  emitMarks,
  emitMarkups,
  emitMarkSelected,
  emitPalette,
  emitHistory,
  emitContext,
  subscribeSelectMark,
  subscribeMarkAction,
  subscribeUndo,
  subscribeRedo,
  type DispoItem,
} from '../lib/evalDispositionBridge';
import {
  addMarkup,
  moveMarkup,
  resizeMarkup,
  removeMarkup,
  setMarkupNote,
  setMarkupLabel,
  zonesForMarkup,
  MARKUP_LABEL,
  type EvalMarkup,
} from '../lib/evalMarkups';
import {
  loadAuoPalette,
  paletteFileFor,
  recordPendingAuo,
  type AuoPalette,
} from '../lib/auoPalette';
import { suggestAuoFixtures, toSuggestion } from '../lib/auoSuggest';
import { logAuoDecision, buildDecisionRecord } from '../lib/auoDecisions';
import {
  saveAuoOverride,
  saveProjectAuoOverride,
  buildOverrideRecord,
  overrideToSuggestion,
  type OverrideDisposition,
} from '../lib/auoOverrides';
import FloorBoundaryTracer from './FloorBoundaryTracer';
import AuoReviewPanel from './AuoReviewPanel';
import UnifiedDispoList, { type UnifiedMarkup } from './evaluate/UnifiedDispoList';
import { subscribeAuoPortalHost } from '../lib/evalAuoPortal';
import { useIsMobile } from '../lib/useIsMobile';

const field: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};
const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink)',
  margin: '0 0 4px',
};

export default function StaffTechnicalPanel({
  clientId,
  projectId,
  actor,
  isAdmin = false,
  onMsg,
  projectImageFiles = [],
  intakeTier,
  floorsOnly = false,
  linkedPropertyIds = [],
  linkedPropertyRefs = [],
  hideGridTool = false,
  hideReportContext = false,
  evalChecklist = false,
  enableZoom = false,
  focusPropertyId,
  workspaceMode = false,
  hideSiteOrientation = false,
  onLinkChange,
}: {
  clientId: string | null;
  /**
   * AD-030 — the project this technical intake belongs to (VP###). When present,
   * intake reads/writes are PROJECT-SCOPED (projects/{pid}/intake/{tier}) with a
   * backward-compatible read fallback + legacy client-doc write mirror. Absent →
   * client-scoped (legacy behaviour, unchanged).
   */
  projectId?: string | null;
  actor: { uid: string | null; name: string };
  /** admin/owner session → may CORRECT an AUO disposition (the override loop).
   *  A non-admin staff member sees the rulings but cannot author corrections
   *  (they flow into every future client's report — authoritative learning data,
   *  admin-gated to match the Firestore rule). */
  isAdmin?: boolean;
  onMsg: (m: string) => void;
  /**
   * Image files already uploaded to THIS project (the Files section). The
   * floor tracer's "Choose from project files" picker lists these so a staff
   * member can start a floor from a plan that is already on the project,
   * not only from a fresh upload. Filtered to images by the caller (a PDF
   * can't be traced as a raster — PDF support is a later item).
   */
  projectImageFiles?: FloorPlanSource[];
  /**
   * Firestore intake doc ID for this project's tier (e.g. "L1A_R1", "L2B_R1").
   * Defaults to L2B_R1 when omitted. Pass the project-tier-specific ID so L1A
   * projects read/write their own intake doc instead of the L2B one.
   */
  intakeTier?: string;
  /**
   * When true, renders ONLY the "Floors & grid (staff)" card — the Site &
   * orientation card, Report context card, and Candidate properties card are
   * suppressed. Used by PropertyDetail to embed just the floor/grid/door
   * machinery without duplicating the site/north/AUO sections that already
   * live on the Property record itself.
   */
  floorsOnly?: boolean;
  /**
   * Property IDs linked to this project (Module 4, AD-013). When non-empty,
   * the "Floors & grid (staff)" section is suppressed — floor management
   * happens on the individual Property pages. A grey note is shown instead.
   * Also suppresses the "Site & orientation" card (property manages site data).
   */
  linkedPropertyIds?: string[];
  /**
   * The project's linked registry properties (projects/{pid}.propertyRefs[]),
   * full shape. Used by the L1A candidate↔property AUTO-JOIN (AD-013 / clean-flow
   * spec §3): when a candidate slot has no `propertyId` FK but the project links a
   * single (or count-matched) registry property, the join stamps it so the marking
   * surface + the report can resolve the gridded `P#####`. Only consumed in the
   * `hideGridTool` + L1A mount; harmless ([]) elsewhere.
   */
  linkedPropertyRefs?: PropertyRefLike[];
  /**
   * ONE GRID PLACE (owner directive 2026-06-26). When true, the per-floor
   * boundary tracer / AUO marking tool is replaced by a pointer to the Property
   * page — gridding happens ONLY on Properties → P#####. The floor combobox +
   * "Push to registry" remain. The project must not be a second tracer.
   */
  hideGridTool?: boolean;
  /**
   * Suppress the in-panel "Report context (staff)" card (clean-flow §6). For the
   * L1A flow the verdict is surfaced in Step 5 (VerdictField) and the rest of
   * report-context lives in the "more" drawer (ReportContextRest) — so this
   * panel must NOT also render it (one editor per field, no double-write).
   */
  hideReportContext?: boolean;
  /**
   * L1A Stage-1R (per-property evaluation). When true, renders a per-property
   * CHECKLIST header above the candidate pills — one row per candidate showing
   * Grid ✓ · Entrance ✓ · AUO ✓/— — so staff complete one property fully, then
   * the next (the owner's per-property-vertical correction). Clicking a row
   * selects that property (switchProperty). Off everywhere else.
   */
  evalChecklist?: boolean;
  /**
   * Phase-1 evaluation workspace (spec §3.3) — pass-through that turns ON in-frame
   * zoom/pan for the mounted FloorBoundaryTracer. Default false → every existing
   * mount (ProjectDetail / L1AFlow) is byte-unchanged.
   */
  enableZoom?: boolean;
  /**
   * Phase-1 evaluation workspace (spec §3.1) — deep-link focus. When the panel is
   * mounted on the `/evaluate/:clientId/:projectId/:propertyId` route, this is the
   * stable registry property (P#####) to open. After load, the matching candidate
   * becomes the ACTIVE property (instead of defaulting to candidate 0) so the
   * workspace shows the deep-linked property. Absent → default candidate 0
   * (existing behaviour, unchanged).
   */
  focusPropertyId?: string;
  /**
   * DEDICATED EVALUATION WORKSPACE (mockup v3.5 / decisions §9 #1–#5). When true
   * the panel renders ONLY the map + marking surface for the focused property's
   * primary floor — the candidate-setup, grid-status rollup, per-property A/B
   * checklist, +Add candidate, property label/address, per-property recommendation
   * and the §D recommended-property verdict are NOT rendered here (they live on the
   * Project page). The marking ENGINE (boundary/door/AUO) is untouched. Default
   * false → every existing mount is byte-unchanged.
   */
  workspaceMode?: boolean;
  /**
   * Hide the project-level "Site & orientation (staff)" card (owner bug
   * 2026-07-03, FIX D). On the L1A Evaluate mount that card writes
   * ENGAGEMENT-level data (client-root north + intake site_location) the L1A
   * per-property grid never reads (the grid uses the property grid's own
   * `northRotation`), and per-property editors already exist (PropertyDetail's
   * SiteLocationCard + the per-candidate north in the workspace) — so it only
   * misleads. Explicit prop, NOT `floorsOnly` (which would also suppress the
   * floors/candidate machinery the Evaluate tab needs). Default false → every
   * non-L1A mount keeps the card byte-unchanged.
   */
  hideSiteOrientation?: boolean;
  /**
   * Bumped after EVERY link-shape write this panel performs (currently: the
   * candidate REMOVE, which deletes the intake slot + its registry link) — the
   * host page's shared `linkRefreshNonce`, so every link-derived read
   * (Evaluate list, grid summary, stage dots, Astro-Vastu, Compare, Verdict)
   * re-resolves in-session (owner ruling 2026-07-03: a correction must
   * propagate). Same contract as PropertyLinkingPanel's onLinkChange.
   */
  onLinkChange?: () => void;
}) {
  // AD-030 — build the intake scope for a (non-null) clientId. Project-scoped
  // when this panel knows its project (VP###); client-only otherwise.
  const scopeFor = (cid: string) => ({
    clientId: cid,
    projectId: projectId || undefined,
  });
  // L1A-gate for the clean-flow behaviour (auto-join + the marking-workspace
  // Layer-1 unblock). Every OTHER tier in the project mount stays byte-identical
  // (the static "Managed on linked Properties" box). `intakeTier` is e.g.
  // "L1A_R1" for an L1A project, "L2B_R1" for L2B.
  const isL1A = (intakeTier || '').toUpperCase().startsWith('L1A');
  const [form, setForm] = useState<StaffIntakeForm>(blankStaffForm());
  // ── MULTI-PROPERTY (L1A — owner ruling D1). `props_` holds every candidate
  // property's metadata + its OWN floors; `activeProp` is the one currently
  // open in the floor machinery below. The existing floor UI keeps editing
  // `form.floors` (the ACTIVE property's working buffer); switchProperty
  // commits `form.floors` back into `props_[activeProp].floors` before swapping
  // in the next property's floors. `saveProperties` assembles them all. A
  // single-property project is just a 1-element `props_` (read-compat). The
  // family + report-context (concerns/verdict/room_overrides) are SHARED. */
  const [props_, setProps_] = useState<PropertyInput[]>([blankProperty()]);
  const [activeProp, setActiveProp] = useState(0);
  // §D cross-property recommendation pick (which candidate is recommended).
  const [recommendedPropId, setRecommendedPropId] = useState('');
  // onMsg is an INLINE callback at the call site (a new reference every
  // render). Keep it in a ref so the load effect can depend on `clientId`
  // ALONE — if onMsg were in the effect deps, every toast (which re-renders
  // the parent → new onMsg) would re-run the load and wipe unsaved floor /
  // north / site edits. (Bug 1 — "uploaded floor vanishes".)
  const onMsgRef = useRef(onMsg);
  onMsgRef.current = onMsg;
  // Site capture is held as raw strings (a coordinate is free-typed); the
  // pure `buildSiteLocation` coerces + range-checks on save / for the buttons.
  const [mapsUrl, setMapsUrl] = useState('');
  // ONE combined "lat, long" coordinate field (Slice 2C). Accepts a decimal
  // pair OR DMS; parsed to canonical decimal by `parseCoordPair`. The owner
  // pastes Google's "lat,long" in and copies the canonical value back out, so
  // the field round-trips with Google. (Replaces the two split lat/lon boxes.)
  const [coords, setCoords] = useState('');
  const [loading, setLoading] = useState(false);
  // The true-north Google-Earth screenshot (durable ref on the client profile).
  const [northShot, setNorthShot] = useState<NorthScreenshot | null>(null);
  const [uploadingShot, setUploadingShot] = useState(false);
  const shotFileRef = useRef<HTMLInputElement | null>(null);
  const [savingNorth, setSavingNorth] = useState(false);
  const [savingFloors, setSavingFloors] = useState(false);
  const [savingSite, setSavingSite] = useState(false);
  // in-flight guard for the candidate REMOVE (FIX A). Declared here — above the
  // auto-join effect, which must hold off while a remove is mid-flight (Vera
  // gate MEDIUM-1): if the registry unlink inside the remove fails, refs still
  // carry the removed P##### for a beat, and the join could re-stamp that id
  // onto a DIFFERENT unstamped candidate and persist it.
  const [removeBusy, setRemoveBusy] = useState(false);
  // ── AD-013 / L1A staff-evaluation MARKING-ON-PROPERTY-GRID ────────────────
  // When `hideGridTool` (the project mount), the staff don't TRACE here — they
  // MARK entrances + AUO on the linked property's already-traced grid. This holds
  // the ACTIVE candidate's resolved property grid (boundary + plan + gridId + the
  // exact engine rotation the Property page used). null = the candidate has no
  // linked property OR its property has no traced grid yet (→ the panel shows a
  // "trace this property's grid first" pointer instead of a dead marking surface).
  const [propGrid, setPropGrid] = useState<PropertyGridForMarking | null>(null);
  const [propGridLoading, setPropGridLoading] = useState(false);
  // guards the boundary-seed effect so it binds the property grid into the active
  // engagement floor ONCE per (candidate, grid), never in a render loop.
  const seededGridRef = useRef<string>('');
  // guards the candidate↔property AUTO-JOIN persist so a given join signature is
  // only written to Firestore once (the join is idempotent, but this stops the
  // setProps_ → re-run loop from re-firing a redundant save).
  const joinPersistSigRef = useRef<string>('');
  // TRUE only once the intake LOAD has replaced the initial `[blankProperty()]`
  // placeholder with the real persisted candidate list (owner bug 2026-07-05).
  // The auto-join persist gates on this (`autoJoinMayPersist`) so it can never
  // fire against the pre-load placeholder — the mount/refresh write that stamped
  // + persisted a fresh blank `floors:[]` candidate and wiped the saved marks.
  const didLoadRef = useRef(false);
  // Slice-7b report context (presenting concerns + property address). Held as
  // raw form text; the pure builder converts the concerns textarea → array on
  // save. Saves independently (its own button), MERGE so floors/site untouched.
  const [reportCtx, setReportCtx] = useState<ReportContextForm>(blankReportContext());
  const [savingReportCtx, setSavingReportCtx] = useState(false);
  // which floor index is mid-upload (for the per-floor "Uploading…" state)
  const [uploadingFloor, setUploadingFloor] = useState<number | null>(null);
  // ── Crop & rotate (map crop/rotate feature). The editor edits the PRISTINE
  // source image and BAKES a derived raster used by the tracer + report (zero
  // coordinate transform — see lib/imageCropRotate). `editFloorIdx` = which
  // floor's plan is open in the editor (null = closed); `editNorthOpen` = the
  // north/location screenshot editor; `baking` disables Apply while we bake+upload.
  const [editFloorIdx, setEditFloorIdx] = useState<number | null>(null);
  const [editNorthOpen, setEditNorthOpen] = useState(false);
  const [baking, setBaking] = useState(false);
  // RESOLVED editable source (2026-06-14 owner fix) — the editor never loads a
  // raw Storage URL itself: we resolve it ONCE here (PDF → rasterise page 1;
  // raster → fetch-as-blob so the canvas never CORS-taints). The editor previews
  // `resolved.previewUrl` (always a displayable raster); the bake reuses
  // `resolved.image` + dims. `resolveErr` surfaces a clear failure in the editor.
  const [resolvedSrc, setResolvedSrc] = useState<EditableSource | null>(null);
  const [resolvingSrc, setResolvingSrc] = useState(false);
  const [resolveErr, setResolveErr] = useState<string | null>(null);
  // ── PDF page picker (2026-06-14 owner fix). A multi-page PDF upload (e.g. a
  // scan whose page 1 is a report cover, the plan on a later page) opens this
  // picker BEFORE rasterising: the staff choose the page, then that page is
  // rasterised → uploaded → traced → reported. A single-page PDF / an image
  // bypasses it entirely (page 1 is rasterised silently). `pagePick` holds the
  // floor index + the PDF File + its bytes (read once) + the page count while
  // the modal is open.
  const [pagePick, setPagePick] = useState<{
    floorIdx: number;
    file: File;
    bytes: ArrayBuffer;
    pageCount: number;
  } | null>(null);
  // The PERSISTED north (after save) drives the quick-launch "True north" chip.
  const [savedNorth, setSavedNorth] = useState<number | null>(null);
  // ── Floors accordion (item 6): exactly ONE floor open at a time (or none).
  const [openFloor, setOpenFloor] = useState<number | null>(null);
  // ── Save status (item 5): unsaved-changes hint + last-saved time. The floors
  // array has ONE owner (saveFloors writes them all), so dirty/savedAt are
  // form-level, surfaced INSIDE the open floor's panel.
  const [floorsDirty, setFloorsDirty] = useState(false);
  const [floorsSavedAt, setFloorsSavedAt] = useState<Date | null>(null);
  // ── AUO (Slice 5): the per-project-type palette + which floor is fetching the
  // engine's disposition suggestions.
  const [palette, setPalette] = useState<AuoPalette | null>(null);
  const [auoBusyFloor, setAuoBusyFloor] = useState<number | null>(null);
  // in-flight guard for the per-property "✦ AI draft" button — disables it +
  // swaps the label to "Drafting…" while generatePropertyRecommendation() is
  // resolving, so a second click can't double-fire / overwrite (WCAG 4.1.3).
  const [recDraftBusy, setRecDraftBusy] = useState(false);
  // the AUO selected on the map / in the side list (two-way reference by number).
  const [selectedAuoId, setSelectedAuoId] = useState<string | null>(null);
  // eval-canvas MARKUPS (annotations) — PERSISTED on the floor (Track A #5):
  // derived from form.floors[0].markups + mutated via patchFloor, so they ride the
  // existing saveFloors path and survive reload (exactly like doors / AUOs).
  const [selectedMarkupId, setSelectedMarkupId] = useState<string | null>(null);
  // mobile breakpoint → the side review list becomes a bottom sheet (a phone
  // can't fit a desktop map + side panel side-by-side). Same 860px constant the
  // app shell uses, so JS + CSS never drift.
  const isMobile = useIsMobile();
  // on a phone, the bottom-sheet review list is collapsed by default (the MAP is
  // the primary surface) and toggled open by a thumb-reachable bar.
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!clientId) return;
    setLoading(true);
    // Re-gate the auto-join for this (client·project·tier) load — it must stay
    // OFF until `setProps_` below lands the real candidate list (owner bug
    // 2026-07-05: the pre-load `[blankProperty()]` placeholder must never be
    // auto-joined + persisted).
    didLoadRef.current = false;
    (async () => {
      try {
        const {
          form: pf,
          recommendedPropertyId,
          site: s,
          northShot: shot,
          projectType,
          reportContext,
        } = await loadPropertiesIntake(scopeFor(clientId), intakeTier);
        if (!alive) return;
        // Load the per-project-type AUO palette (fetched static asset; falls back
        // to residence + inline-add, so a miss never blocks marking).
        loadAuoPalette(projectType)
          .then((p) => alive && setPalette(p))
          .catch(() => {});
        // Bug-2: clean each floor's boundary ONCE on load (+ re-map its doors)
        // so the live grid renders byte-identical in shape to the v7 preview
        // immediately. Applied PER PROPERTY (each property owns its floors).
        const cleanFloors = (fls: FloorInput[]) =>
          fls.map((fl) => {
            const { boundary, doors } = cleanFloorBoundary(fl.boundary, fl.doors);
            return { ...fl, boundary, doors };
          });
        // ZERO CANDIDATES IS A REAL STATE for L1A (owner bug 2026-07-03,
        // FIX A): after "Remove property" deleted the last candidate, the
        // persisted `answers.properties` is [] and must LOAD as [] — padding a
        // blankProperty back in would resurrect a phantom slot. Non-L1A keeps
        // the single-property pad (its floor machinery expects one slot).
        const cleanedProps: PropertyInput[] = (pf.properties.length
          ? pf.properties
          : isL1A
            ? []
            : [blankProperty()]
        ).map((p) => ({ ...p, floors: cleanFloors(p.floors) }));
        setProps_(cleanedProps);
        // Phase-1 deep-link focus (spec §3.1): open the route's property when
        // given + present; else default to candidate 0 (existing behaviour).
        const focusIdx = focusPropertyId
          ? cleanedProps.findIndex((p) => p.propertyId === focusPropertyId)
          : -1;
        // Default to the first LINK-VISIBLE candidate in the Evaluate-tab
        // mount (owner ruling 2026-07-03): a candidate whose stamped property
        // was unlinked at Intake is dropped from the list below, so it must
        // not be the silent default selection either. With an EMPTY refs list
        // every candidate is visible (VP678 no-op rule) ⇒ index 0, unchanged
        // behaviour. -1 (none visible under a non-empty refs list) falls back
        // to 0 — the list renders nothing and no phantom row is offered.
        const firstVisibleIdx = evalChecklist
          ? cleanedProps.findIndex((c) => isCandidateLinkVisible(c, linkedPropertyIds))
          : 0;
        const startIdx = focusIdx >= 0 ? focusIdx : Math.max(0, firstVisibleIdx);
        setActiveProp(startIdx);
        setRecommendedPropId(recommendedPropertyId || '');
        // The active property's floors + its north drive the existing floor UI.
        // ZERO candidates (L1A explicit-empty) → an empty, inert buffer.
        const first = cleanedProps[startIdx];
        setForm({
          north_rotation: first?.north_rotation || pf.north_rotation || '',
          floors: first?.floors ?? [],
        });
        setMapsUrl(s.google_maps_url);
        // Seed the combined field with the canonical "lat, long" string so it
        // round-trips with Google on copy-out (empty when no coords stored).
        setCoords(formatCoordPair(s.lat, s.lon));
        setNorthShot(shot);
        setReportCtx(reportContext || blankReportContext());
        setSavedNorth(normaliseNorth(first?.north_rotation || pf.north_rotation));
        // freshly loaded = clean; collapse all floors (item 5 + 6).
        setFloorsDirty(false);
        setFloorsSavedAt(null);
        setOpenFloor(null);
        // The real candidate list is now in props_ — OPEN the auto-join gate.
        // The effect re-runs on this load's setProps_/setLoading and then joins
        // the REAL candidate (never the pre-load placeholder).
        didLoadRef.current = true;
      } catch {
        if (alive)
          onMsgRef.current(
            'Could not load technical intake (check connection).',
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // Deps = [clientId, projectId, intakeTier]. onMsg is read via
    // onMsgRef.current (a stable ref, exempt from exhaustive-deps), so a toast
    // no longer re-runs the load + wipes unsaved floor / north / site edits
    // (Bug 1 fix). projectId is in the deps so switching the project scope
    // (AD-030) re-reads the right intake doc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId, intakeTier]);

  // ── AD-013 / L1A — AUTO-JOIN candidate slots to linked registry properties. ──
  // The candidate the staff grade (answers.properties[i]) must carry the FK to the
  // linked, gridded P##### so the marking surface + the report resolve the grid.
  // Linking a property (projects.propertyRefs[]) does NOT stamp the FK on its own;
  // this bridges it. PURE join (lib/candidatePropertyJoin) — auto-stamps ONLY when
  // unambiguous, leaves ambiguity for the manual picker. Runs after the initial
  // load (props_ set) AND whenever a property is linked while the panel is open
  // (linkedPropertyRefs changes). Idempotent + a per-signature persist guard →
  // converges, never loops, never clobbers in-progress floor edits (we commit the
  // active buffer before saving). L1A + hideGridTool only.
  useEffect(() => {
    // removeBusy hold-off (Vera gate MEDIUM-1, 2026-07-03): while a candidate
    // REMOVE is mid-flight the refs prop can still carry the removed P#####
    // (the unlink lands after the intake persist — or not at all on its
    // failure path), and joining against that stale set could re-stamp the
    // removed id onto a DIFFERENT unstamped candidate and persist it. The
    // effect re-runs when removeBusy flips false (it is in the deps), joining
    // against the then-current refs.
    // Gate (owner bug 2026-07-05): never auto-join+persist until the load has
    // landed the real candidate list (`didLoadRef`). On the mount commit `props_`
    // is still `[blankProperty()]` and `loading` is stale-false, so without this
    // the effect stamped + persisted a fresh blank floors:[] candidate, wiping
    // the saved marks and churning the candidate id on every mount/refresh.
    if (
      !autoJoinMayPersist({
        hideGridTool,
        isL1A,
        clientId,
        loading,
        removeBusy,
        loaded: didLoadRef.current,
      })
    )
      return;
    if (!clientId) return; // narrow for scopeFor (autoJoinMayPersist already ensured it)
    const { joined, changed } = joinCandidatesToLinkedProperties(
      props_,
      linkedPropertyRefs,
    );
    if (!changed) return;
    const sig = joined.map((c) => `${c.id}:${c.propertyId || ''}`).join('|');
    if (joinPersistSigRef.current === sig) return;
    joinPersistSigRef.current = sig;
    setProps_(joined);
    // Persist so the composer's answers.properties[] carries the FK on the next
    // compose. Bug 1 fix: persist `joined` DIRECTLY — it carries each candidate's
    // already-STORED floors. The auto-join's ONLY job is to stamp the propertyId
    // FK; it must NOT fold the LIVE `form.floors` buffer in, because on first mount
    // after linking that buffer is still [] (the boundary-seed effect hasn't run
    // yet) → committing it would persist floors:[] and drop the entrance. The
    // user's explicit onSaveFloors is the SOLE owner of floor persistence.
    void saveProperties(
      scopeFor(clientId),
      { north_rotation: form.north_rotation, properties: joined },
      { uid: actor.uid, via: 'staff' },
      { recommendedPropertyId: recommendedPropId },
      intakeTier,
    ).catch((e) =>
      onMsgRef.current(`Auto-link save failed: ${(e as Error).message}`),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideGridTool, isL1A, clientId, loading, removeBusy, props_, linkedPropertyRefs]);

  // ── AD-013 / L1A — load the ACTIVE candidate's property grid for marking. ──
  // Only in MARKING-ONLY mode (hideGridTool, the project mount). Reads the linked
  // property (P#####), resolves its active concept▸version▸floor grid, and holds
  // it so the staff mark entrances/AUO on that locked grid. Re-runs whenever the
  // active candidate (or its linked property) changes. No property / no traced
  // grid → propGrid stays null (the panel shows a "trace it first" pointer).
  const activePropertyId = props_[activeProp]?.propertyId || '';
  useEffect(() => {
    if (!hideGridTool || !activePropertyId) {
      setPropGrid(null);
      setPropGridLoading(false);
      seededGridRef.current = '';
      return;
    }
    let alive = true;
    setPropGridLoading(true);
    setPropGrid(null);
    seededGridRef.current = '';
    (async () => {
      try {
        const prop = await getProperty(activePropertyId);
        if (!alive) return;
        setPropGrid(resolvePropertyGridForMarking(prop));
      } catch {
        if (alive) setPropGrid(null);
      } finally {
        if (alive) setPropGridLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [hideGridTool, activePropertyId]);

  // ── AD-013 / L1A — SEED the property grid into the active engagement floor. ──
  // The marking handlers + the tracer read `form.floors[fi].boundary`/`.floorplan`;
  // the property boundary lives in the property plan's IMAGE-PIXEL space, so we
  // feed BOTH the boundary AND the active plan in as a closed, LOCKED boundary.
  // We bind the PRIMARY surface (floor 0) — an L1A candidate is single-surface (a
  // site/plot map or one floor). Seeding once per (candidate, grid) keeps the
  // intake floor carrying the canonical boundary (report-read alignment) and never
  // loops. Marks the form dirty so the binding persists with the next save.
  useEffect(() => {
    if (!hideGridTool || !propGrid) return;
    const key = `${propGrid.propertyId}::${propGrid.gridId ?? propGrid.floorId}::${propGrid.boundary.length}::${propGrid.needsBoundary ? 'plan' : 'grid'}`;
    if (seededGridRef.current === key) return;
    let changed = false;
    setForm((f) => {
      const floors = f.floors.length ? f.floors.slice() : [blankFloor()];
      const cur = floors[0];
      // PLAN-ONLY (Gap #1): seed the floorplan so the map is VISIBLE, but DON'T
      // seed a boundary (there is none) and DON'T clobber any existing one — the
      // floor stays not grid-ready so it never counts as "evaluated".
      const nextBoundary = propGrid.needsBoundary
        ? (cur.boundary ?? [])
        : (propGrid.boundary as Pt[]);
      const sameBoundary = propGrid.needsBoundary
        ? true
        : JSON.stringify(cur.boundary ?? null) === JSON.stringify(propGrid.boundary);
      const samePlan =
        (cur.floorplan?.downloadURL ?? '') === (propGrid.floorPlan?.downloadURL ?? '');
      if (sameBoundary && samePlan && !!cur.name) return f; // already bound
      changed = true;
      floors[0] = {
        ...cur,
        name: cur.name || propGrid.floorLabel,
        boundary: nextBoundary,
        floorplan: propGrid.floorPlan ?? cur.floorplan ?? null,
      };
      return { ...f, floors };
    });
    seededGridRef.current = key;
    if (changed) setFloorsDirty(true);
  }, [hideGridTool, propGrid]);

  /* ── north ── */
  const northVal = normaliseNorth(form.north_rotation);
  const northInvalid = form.north_rotation.trim() !== '' && northVal == null;

  async function onSaveNorth() {
    if (!clientId || northVal == null) return;
    setSavingNorth(true);
    try {
      await saveNorthRotation(clientId, northVal, actor);
      setSavedNorth(northVal);
      onMsg(`North bearing saved (${northVal}°) to the client profile.`);
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSavingNorth(false);
    }
  }

  /** Upload the true-north Google-Earth screenshot, persist its durable ref to
   *  the client profile (so it survives reload + is report-accessible). */
  async function onUploadNorthShot(file: File) {
    if (!clientId) return;
    setUploadingShot(true);
    try {
      const shot = await uploadNorthScreenshot(clientId, file);
      await saveNorthScreenshot(clientId, shot, actor);
      setNorthShot(shot);
      onMsg('North screenshot uploaded.');
    } catch (e) {
      onMsg(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploadingShot(false);
    }
  }

  /** CROP (+ optional rotate) the north / location screenshot — the §B1 case:
   *  the Google-Earth screenshot was captured WITH the Measure tool panel
   *  visible; staff crop it out. Bakes a derived image (NO width/height needed —
   *  it's a reference image, not a tracing surface) and re-points the profile ref
   *  at it. The screenshot has no boundary/grid coupling, so there is nothing to
   *  invalidate. The editor edits FROM the current screenshot (no original-ref
   *  chain kept here — a north screenshot is a reference, re-croppable from the
   *  last upload). */
  async function onApplyNorthEdit(recipe: EditRecipe) {
    if (!clientId || !northShot?.download_url) return;
    if (!resolvedSrc) {
      onMsg('Still preparing the image — try again in a moment.');
      return;
    }
    setBaking(true);
    setUploadingShot(true);
    try {
      // Reuse the already-resolved raster (PDF-rasterised / CORS-safe). The bake
      // mime follows the resolved source (a PDF page bakes to JPEG).
      const name = derivedFileName(
        northShot.storage_path.split('/').pop() || 'north',
        resolvedSrc.bakeMime,
      );
      const baked = await bakeImage(
        resolvedSrc.image,
        resolvedSrc.width,
        resolvedSrc.height,
        recipe,
        name,
        resolvedSrc.bakeMime,
      );
      const shot = await uploadDerivedNorthScreenshot(clientId, baked.file);
      await saveNorthScreenshot(clientId, shot, actor);
      setNorthShot(shot);
      setEditNorthOpen(false);
      onMsg('Edited north screenshot saved.');
    } catch (e) {
      onMsg(`Could not apply the edit: ${(e as Error).message}`);
    } finally {
      setBaking(false);
      setUploadingShot(false);
    }
  }

  /** Remove the stored north screenshot (clears the profile ref; the Storage
   *  object is left in place — harmless, overwritten on the next upload). */
  async function onRemoveNorthShot() {
    if (!clientId) return;
    try {
      await saveNorthScreenshot(clientId, null, actor);
      setNorthShot(null);
      onMsg('North screenshot removed.');
    } catch (e) {
      onMsg(`Could not remove: ${(e as Error).message}`);
    }
  }

  /* ── candidate properties (multi-property, owner ruling D1) ──
   * The floor machinery below always edits `form.floors` (the ACTIVE property's
   * working buffer) + `form.north_rotation` (its bearing). These helpers commit
   * that buffer back into `props_[activeProp]` and swap properties in/out. */
  function commitActiveIntoProps(
    list: PropertyInput[] = props_,
  ): PropertyInput[] {
    const next = list.slice();
    if (next[activeProp]) {
      next[activeProp] = {
        ...next[activeProp],
        // Bug 1 belt-and-braces: NON-DESTRUCTIVE floor commit. Only fold the live
        // buffer over the stored floors when it actually holds floors; an empty
        // buffer (pre-seed, before the property grid has hydrated) must NOT wipe a
        // candidate's real stored floors. Protects every caller of this helper.
        floors: form.floors.length > 0 ? form.floors : next[activeProp].floors,
        north_rotation: form.north_rotation,
      };
    }
    return next;
  }
  function switchProperty(idx: number) {
    if (idx === activeProp) return;
    const committed = commitActiveIntoProps();
    const target = committed[idx];
    if (!target) return;
    setProps_(committed);
    setActiveProp(idx);
    setForm({ north_rotation: target.north_rotation || '', floors: target.floors });
    setSavedNorth(normaliseNorth(target.north_rotation));
    setOpenFloor(null);
    setFloorsDirty(false);
  }
  // Correction propagation (owner ruling 2026-07-03), Evaluate-tab mount only:
  // if the ACTIVE candidate's linked property is UNLINKED at Intake while this
  // panel is mounted, its row is dropped from the merged candidate list (the
  // isCandidateLinkVisible render filter below) — so move the selection to the
  // first still-visible candidate, or the marking surface would keep showing a
  // row the list no longer offers. switchProperty commits the working buffer
  // first, so nothing in-progress is lost. `linkedPropertyIds` is the LIVE
  // projects.propertyRefs[] projection from the host page's project stream.
  useEffect(() => {
    if (!evalChecklist || loading) return;
    const active = props_[activeProp];
    if (!active || isCandidateLinkVisible(active, linkedPropertyIds)) return;
    const next = props_.findIndex((c) => isCandidateLinkVisible(c, linkedPropertyIds));
    if (next >= 0 && next !== activeProp) switchProperty(next);
    // switchProperty is a stable in-render function (not a hook dep by design,
    // matching the other effects in this file).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalChecklist, loading, props_, activeProp, linkedPropertyIds]);
  function addProperty() {
    const committed = commitActiveIntoProps();
    const np = blankProperty();
    const next = [...committed, np];
    setProps_(next);
    setActiveProp(next.length - 1);
    setForm({ north_rotation: '', floors: [] });
    setSavedNorth(null);
    setOpenFloor(null);
    setFloorsDirty(true);
  }
  /**
   * REMOVE a candidate — a TRUE, persisted delete (owner bug 2026-07-03,
   * FIX A). The old version only mutated local state (a refresh restored the
   * candidate) and hard-blocked removing the last one. Now:
   *   1. commit the active buffer, drop the slot, and PERSIST the shortened
   *      array immediately (same `saveProperties` array-replace as
   *      `onSaveFloors` — this IS the delete);
   *   2. if the candidate carried a registry stamp, remove that id from
   *      `projects.propertyRefs[]` too (`unlinkPropertyFromProject` — Lane B
   *      stays consistent; the id being absent from refs is a clean no-op,
   *      the VP678 shape);
   *   3. removing down to ZERO candidates is allowed — the empty state renders
   *      an explicit "no candidates" note pointing at the Intake link path;
   *   4. bump the shared linkRefreshNonce (a removal is a link-shape write) so
   *      Evaluate / Astro-Vastu / Compare / Verdict re-resolve in-session.
   * (`removeBusy` is declared up with the other saving flags — the auto-join
   * effect above must read it, so it lives above that effect.)
   */
  async function removeProperty(idx: number) {
    if (!clientId || removeBusy) return;
    const committed = commitActiveIntoProps();
    const removed = committed[idx];
    if (!removed) return;
    const remaining = committed.filter((_, j) => j !== idx);
    // Clear the §D winner if the candidate being removed IS the recommended one.
    // Back-compat: the winner now keys on stable `propertyId` (`P#####`) but a
    // legacy value may still be the candidate slot `id` (`prop_…`) — match either.
    const nextRecommended =
      recommendedPropId &&
      (removed.propertyId === recommendedPropId || removed.id === recommendedPropId)
        ? ''
        : recommendedPropId;
    setRemoveBusy(true);
    try {
      // (1) the true delete — array-replace `answers.properties` WITHOUT the slot.
      await saveProperties(
        scopeFor(clientId),
        { north_rotation: form.north_rotation, properties: remaining },
        { uid: actor.uid, via: 'staff' },
        { recommendedPropertyId: nextRecommended },
        intakeTier,
      );
      // (2) Lane B — drop the registry link too. Tolerant: on projects whose
      // refs lane was never maintained (VP678) the id simply isn't there and
      // the read-modify-write is a no-op; a failure here must not undo the
      // already-persisted intake delete, so it degrades to a warning.
      if (removed.propertyId && projectId) {
        try {
          await unlinkPropertyFromProject(projectId, removed.propertyId);
        } catch (e) {
          console.warn(
            `[removeProperty] registry unlink ${removed.propertyId} failed (candidate slot already removed):`,
            (e as Error).message,
          );
        }
      }
      // (3) local state AFTER the persist landed — active index clamped into
      // the shortened (possibly empty) list; empty ⇒ an inert blank buffer.
      // NON-L1A mounts keep the single-property pad (a fresh blank slot —
      // exactly what a reload gives them via the load-effect pad), so their
      // floor machinery never sees a zero-length list; L1A keeps the honest
      // empty state (candidates re-enter only via the Intake link path).
      const remainingForState =
        !isL1A && remaining.length === 0 ? [blankProperty()] : remaining;
      const nextActive = Math.min(
        idx <= activeProp ? Math.max(0, activeProp - 1) : activeProp,
        Math.max(0, remainingForState.length - 1),
      );
      const target = remainingForState[nextActive];
      setProps_(remainingForState);
      setActiveProp(nextActive);
      setForm({ north_rotation: target?.north_rotation || '', floors: target?.floors || [] });
      setSavedNorth(normaliseNorth(target?.north_rotation));
      setOpenFloor(null);
      setRecommendedPropId(nextRecommended);
      setFloorsDirty(false);
      // (4) the shared link-correction nonce — every stage tab re-reads.
      onLinkChange?.();
      onMsg(
        `Removed ${removed.label?.trim() || 'the candidate property'}${
          removed.propertyId ? ` (unlinked ${removed.propertyId})` : ''
        }.`,
      );
    } catch (e) {
      onMsg(`Remove failed: ${(e as Error).message}`);
    } finally {
      setRemoveBusy(false);
    }
  }
  // Two-step INLINE confirm for the destructive remove (never window.confirm —
  // blocks automation + off-style): first click arms the button ("Confirm
  // remove?"), the second click within 4s executes; the arm times out back to
  // the idle label and disarms when the active candidate switches.
  const [removeArmed, setRemoveArmed] = useState(false);
  const removeArmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (removeArmTimer.current) clearTimeout(removeArmTimer.current);
    },
    [],
  );
  useEffect(() => {
    setRemoveArmed(false);
  }, [activeProp]);
  function onRemoveClick() {
    if (removeBusy) return;
    if (!removeArmed) {
      setRemoveArmed(true);
      if (removeArmTimer.current) clearTimeout(removeArmTimer.current);
      removeArmTimer.current = setTimeout(() => setRemoveArmed(false), 4000);
      return;
    }
    if (removeArmTimer.current) clearTimeout(removeArmTimer.current);
    setRemoveArmed(false);
    void removeProperty(activeProp);
  }
  function patchPropertyMeta(idx: number, patch: Partial<PropertyInput>) {
    setProps_((list) => {
      const next = list.slice();
      if (next[idx]) next[idx] = { ...next[idx], ...patch };
      return next;
    });
    setFloorsDirty(true);
  }
  const multiProperty = props_.length > 1;

  // NOTE (2026-07-01): the per-candidate grid-analysis rollup that used to live
  // here (a `candidateGrid = gridSummary(...)` memo feeding a strip in the
  // candidate box) was REMOVED — it duplicated the identical strip in the merged
  // L1A "Evaluate" header (ProjectDetail, reading the same gridSummary). One
  // rollup, one place. No info lost.

  /* ── floors ── */
  // The grid + engine consume the engine-ROTATION (= 360 − bearing), NOT the
  // owner-facing bearing held in the form / savedNorth (item 1 fix). Convert the
  // live bearing → rotation so the on-screen grid orients EXACTLY like the report
  // engine (which reads the stored rotation). Persisted bearing wins, else the
  // in-form bearing.
  const bearingForGrid = savedNorth ?? normaliseNorth(form.north_rotation);
  // In MARKING-ONLY mode (hideGridTool + a resolved property grid) the marks must
  // derive against the EXACT grid the Property page produced — same boundary AND
  // same rotation. Use the property grid's stored engine rotation verbatim so the
  // padas/zones the staff confirm match the property grid byte-for-byte. Otherwise
  // (the full tracer) convert the owner-facing bearing → engine rotation as before.
  const northForGrid =
    hideGridTool && propGrid
      ? propGrid.northRotation
      : bearingToRotation(bearingForGrid);

  function patchFloor(i: number, p: Partial<FloorInput>) {
    setForm((f) => {
      const next = f.floors.slice();
      next[i] = { ...next[i], ...p };
      return { ...f, floors: next };
    });
    setFloorsDirty(true);
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * UNDO / REDO — bounded mark history (eval workspace only). The owner wants to
   * recover a deleted mark AND step forward again, so this is a proper
   * back/forward history (NOT undo-only): a `past` stack + a `future` (redo)
   * stack, each bounded to UNDO_DEPTH. We snapshot the PERSISTED floor-0 arrays
   * {doors, fixtures, markups} (NOT the derived AuoMark[]) BEFORE each discrete
   * mutating op; restoring re-applies a snapshot through patchFloor → save, and
   * the derived ids re-derive index-based on restore, so restoring the persisted
   * arrays is correct. Continuous ops (drag/resize) are intentionally NOT
   * snapshotted (they would flood the bounded history one pointer-tick at a time).
   * Covers all three mark types — entrances, AUOs, markups. No-op off enableZoom.
   * ──────────────────────────────────────────────────────────────────────── */
  type FloorMarkSnapshot = Pick<FloorInput, 'doors' | 'fixtures' | 'markups'>;
  const UNDO_DEPTH = 5;
  const undoPast = useRef<FloorMarkSnapshot[]>([]);
  const undoFuture = useRef<FloorMarkSnapshot[]>([]);

  // detached deep copy of one persisted array (guards undefined — JSON of
  // undefined throws). Marks are plain data, so a JSON round-trip is sufficient.
  const cloneArr = <T,>(a: T[] | undefined): T[] | undefined =>
    a ? (JSON.parse(JSON.stringify(a)) as T[]) : undefined;

  function snapshotFloor0(): FloorMarkSnapshot | null {
    const fl = form.floors[0];
    if (!fl) return null;
    return { doors: cloneArr(fl.doors), fixtures: cloneArr(fl.fixtures), markups: cloneArr(fl.markups) };
  }

  function emitHistoryState() {
    if (!enableZoom) return;
    emitHistory({ canUndo: undoPast.current.length > 0, canRedo: undoFuture.current.length > 0 });
  }

  /** Capture the pre-mutation floor-0 arrays BEFORE a discrete mark edit. A NEW
   *  edit clears the redo stack (standard back/forward semantics — you can't redo
   *  into a branch you've abandoned). No-op off the eval workspace. */
  function pushUndoSnapshot() {
    if (!enableZoom) return;
    const snap = snapshotFloor0();
    if (!snap) return;
    undoPast.current = [...undoPast.current, snap].slice(-UNDO_DEPTH);
    undoFuture.current = [];
    emitHistoryState();
  }

  /** Undo: push the current state onto redo, pop the last past snapshot, apply it. */
  function restorePrevious() {
    if (!enableZoom || undoPast.current.length === 0) return;
    const current = snapshotFloor0();
    const prev = undoPast.current[undoPast.current.length - 1];
    undoPast.current = undoPast.current.slice(0, -1);
    if (current) undoFuture.current = [...undoFuture.current, current].slice(-UNDO_DEPTH);
    patchFloor(0, { doors: prev.doors, fixtures: prev.fixtures, markups: prev.markups });
    emitHistoryState();
  }

  /** Redo: push the current state onto past, pop the last future snapshot, apply it. */
  function restoreNext() {
    if (!enableZoom || undoFuture.current.length === 0) return;
    const current = snapshotFloor0();
    const next = undoFuture.current[undoFuture.current.length - 1];
    undoFuture.current = undoFuture.current.slice(0, -1);
    if (current) undoPast.current = [...undoPast.current, current].slice(-UNDO_DEPTH);
    patchFloor(0, { doors: next.doors, fixtures: next.fixtures, markups: next.markups });
    emitHistoryState();
  }

  // keep the toolbar's undo/redo wired to the LATEST restore closures (fresh
  // `form`) without re-subscribing on every render — the listeners read a ref.
  const restoreRef = useRef<{ undo: () => void; redo: () => void }>({ undo: () => {}, redo: () => {} });
  restoreRef.current = { undo: restorePrevious, redo: restoreNext };
  useEffect(() => {
    if (!enableZoom) return;
    const u = subscribeUndo(() => restoreRef.current.undo());
    const r = subscribeRedo(() => restoreRef.current.redo());
    // emit the initial (empty) history so the toolbar starts with both disabled.
    emitHistory({ canUndo: undoPast.current.length > 0, canRedo: undoFuture.current.length > 0 });
    return () => {
      u();
      r();
    };
  }, [enableZoom]);

  function addFloor() {
    // "+ Add floor" appends a COLLAPSED row, then opens it (item 6 — one open).
    setForm((f) => ({ ...f, floors: [...f.floors, blankFloor()] }));
    setOpenFloor(form.floors.length); // the new row's index
    setFloorsDirty(true);
  }
  function removeFloor(i: number) {
    setForm((f) => ({ ...f, floors: f.floors.filter((_, j) => j !== i) }));
    setOpenFloor((cur) =>
      cur == null ? null : cur === i ? null : cur > i ? cur - 1 : cur,
    );
    setFloorsDirty(true);
  }
  /** Accordion toggle — open this floor (collapsing any other), or close it. */
  function toggleFloor(i: number) {
    setOpenFloor((cur) => (cur === i ? null : i));
  }

  /** Upload a floor-plan image (or PDF) for floor `fi` and attach it to that
   *  floor. A MULTI-PAGE PDF is intercepted: we detect the page count and, when
   *  >1, open the page picker so the staff choose the page that holds the plan
   *  (the chosen page is rasterised + uploaded in `onConfirmPdfPage`). An image
   *  OR a single-page PDF uploads straight through (page 1 rasterised silently). */
  async function onUploadPlan(fi: number, file: File) {
    if (!clientId) return;
    const fl = form.floors[fi];
    // Multi-page PDF → let the staff choose the page first (don't blindly take
    // page 1). Read the bytes ONCE here and hand them to the picker (which slices
    // a copy per render — pdfjs neuters the buffer it is given).
    if (isPdfFloorPlanType(file.type)) {
      setUploadingFloor(fi);
      try {
        const bytes = await file.arrayBuffer();
        const pageCount = await getPdfPageCount(bytes.slice(0));
        if (pageCount > 1) {
          setUploadingFloor(null);
          setPagePick({ floorIdx: fi, file, bytes, pageCount });
          return; // the picker drives the rest (onConfirmPdfPage)
        }
        // single-page PDF → upload page 1 as before (we already have the bytes,
        // but uploadFloorPlan re-reads the File; cheap for one page).
        const fp = await uploadFloorPlan(clientId, fl.id, file, 1);
        patchFloor(fi, { floorplan: fp });
        onMsg('Floor plan uploaded. Trace the outline, then save the floor.');
      } catch (e) {
        onMsg(`Upload failed: ${(e as Error).message}`);
      } finally {
        setUploadingFloor(null);
      }
      return;
    }
    setUploadingFloor(fi);
    try {
      const fp = await uploadFloorPlan(clientId, fl.id, file);
      patchFloor(fi, { floorplan: fp });
      onMsg('Floor plan uploaded. Trace the outline, then save the floor.');
    } catch (e) {
      onMsg(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploadingFloor(null);
    }
  }

  /** Page picker confirmed → rasterise the CHOSEN page of the held PDF, upload it
   *  as the floor's plan, and close the picker. Errors keep the picker open so the
   *  staff can retry / pick another page. */
  async function onConfirmPdfPage(pageNum: number) {
    if (!clientId || !pagePick) return;
    const fi = pagePick.floorIdx;
    const fl = form.floors[fi];
    if (!fl) {
      setPagePick(null);
      return;
    }
    setUploadingFloor(fi);
    try {
      const fp = await uploadFloorPlan(clientId, fl.id, pagePick.file, pageNum);
      patchFloor(fi, { floorplan: fp });
      setPagePick(null);
      onMsg(
        `Floor plan uploaded (page ${pageNum}). Trace the outline, then save the floor.`,
      );
    } catch (e) {
      onMsg(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploadingFloor(null);
    }
  }

  /** Attach a plan from a file ALREADY uploaded to the project (the Files
   *  section) — no fresh upload. Reads its natural pixel size from the
   *  download URL (Image naturalWidth/Height — no canvas, no CORS). */
  async function onPickProjectFile(fi: number, src: FloorPlanSource) {
    setUploadingFloor(fi);
    try {
      const fp = await floorPlanFromProjectFile(src);
      patchFloor(fi, { floorplan: fp });
      onMsg('Floor plan attached from project files. Trace the outline, then save the floor.');
    } catch (e) {
      onMsg(`Could not attach that file: ${(e as Error).message}`);
    } finally {
      setUploadingFloor(null);
    }
  }

  function onChangeBoundary(fi: number, rawBoundary: Pt[], commit = false) {
    const fl = form.floors[fi];
    // CROSS-LINE FIX (2026-06-08): the clean (dedupe + CW-winding normalise) used
    // to run on EVERY boundary change — including each mid-trace click. While the
    // outline was still open, `cleanBoundary` could REVERSE the partial polygon's
    // winding between two clicks; the next appended point then no longer followed
    // the previous one in click order, so the drawn (auto-closing) polygon linked
    // a later point back to an earlier one — a self-intersecting "cross line"
    // (owner: "after the 5th point, the 6th links from the 4th"). FIX: while
    // tracing (commit=false) store the points VERBATIM in click order; run the
    // clean ONLY on COMMIT (boundary closed / vertex-drag ended) — exactly when a
    // CW normalise is meaningful and stable. The committed shape stays
    // byte-identical to the v7 preview; the live trace stays in pure click order.
    if (!commit) {
      patchFloor(fi, { boundary: rawBoundary });
      return;
    }
    const { boundary, doors: remappedDoors } = cleanFloorBoundary(
      rawBoundary,
      fl.doors,
    );
    // The grid moved → re-derive every door's centre + covered padas off the new
    // grid/boundary so the on-map padas always reflect the current boundary (the
    // trust signal). marksFromFloor reads the RE-MAPPED doors so a dedupe/reverse
    // never strands a door on the wrong wall.
    const grid = gridFor(boundary, northForGrid);
    const marks = marksFromFloor(
      { ...fl, doors: remappedDoors },
      grid,
      boundary,
    );
    // AUO marks live in image space; the grid moved → re-derive each zone off the
    // new boundary so the on-map zone always reflects the current grid (a
    // re-derive that changes a confirmed mark's zone re-opens it).
    const auoMarks = rederiveAuoMarks(auoMarksFor(fl), grid);
    const auoPatch =
      auoMarksFor(fl).length > 0 ? { fixtures: marksToFixtures(auoMarks) } : {};
    if (marks.length) {
      patchFloor(fi, {
        boundary,
        ...marksToFloorPatch(rederiveMarks(marks, grid, boundary)),
        ...auoPatch,
      });
    } else {
      patchFloor(fi, { boundary, doors: remappedDoors, ...auoPatch });
    }
  }
  function onClearPlan(fi: number) {
    patchFloor(fi, { floorplan: null });
  }

  /* ── CROP & ROTATE (non-destructive bake) ──────────────────────────────────
   *  Edit the floor's PRISTINE source image, bake a derived raster (crop →
   *  rotate), upload it to the same floorplans/{floorId}/ path (a `derived-…`
   *  name beside the original), and point the floor's `floorplan` at it. The
   *  tracer + report consume the derived image with ZERO coordinate transform
   *  (its baked width/height ARE the tracing space). Non-destructive: the
   *  original ref + recipe ride on `floorplan.{original,edit}` so the editor
   *  re-opens against the source and the un-edited image is recoverable.
   *
   *  RE-EDIT INVALIDATES THE TRACE: the baked frame has new pixel dimensions, so
   *  a boundary traced on the OLD frame no longer aligns. We DROP the boundary +
   *  every door/AUO mark (all live in the old image-pixel space) and tell the
   *  staff to re-trace. (Documented behaviour — re-trace, not silent drift.) */
  function openPlanEditor(fi: number) {
    const fl = form.floors[fi];
    if (!fl?.floorplan?.downloadURL) {
      onMsg('Upload a floor plan first, then crop & rotate it.');
      return;
    }
    setEditFloorIdx(fi);
  }

  // RESOLVE the editable source whenever an editor opens (2026-06-14 owner fix).
  // The source is read through the Firebase Storage SDK (getBlob on the
  // storagePath) — an authenticated SDK read that sidesteps the bucket's missing
  // CORS headers entirely, so the bake canvas never taints (the ROOT cause of
  // "Could not load that file to edit"). A PDF is rasterised page 1 → canvas.
  // The editor previews `resolvedSrc.previewUrl`; the bake reuses
  // `resolvedSrc.image`. Revokes the object URL on close/change.
  const editFloorSource = (() => {
    if (editFloorIdx == null) return null;
    const fp = form.floors[editFloorIdx]?.floorplan;
    if (!fp) return null;
    const ref = sourceRefFor(fp);
    return {
      url: ref.downloadURL,
      contentType: ref.contentType,
      storagePath: ref.storagePath,
    };
  })();
  const northSource =
    editNorthOpen && northShot?.download_url
      ? {
          url: northShot.download_url,
          contentType: 'image/png',
          storagePath: northShot.storage_path || '',
        }
      : null;
  const activeSource = editFloorSource ?? northSource;
  const activeSourceUrl = activeSource?.url ?? '';
  const activeSourceType = activeSource?.contentType ?? '';
  const activeSourcePath = activeSource?.storagePath ?? '';

  useEffect(() => {
    if (!activeSourceUrl && !activeSourcePath) {
      setResolvedSrc(null);
      setResolveErr(null);
      setResolvingSrc(false);
      return;
    }
    let cancelled = false;
    let resolved: EditableSource | null = null;
    setResolvingSrc(true);
    setResolveErr(null);
    setResolvedSrc(null);
    (async () => {
      try {
        const r = await loadEditableSource(
          activeSourceUrl,
          activeSourceType,
          activeSourcePath,
        );
        if (cancelled) {
          r.revoke();
          return;
        }
        resolved = r;
        setResolvedSrc(r);
      } catch (e) {
        if (!cancelled) setResolveErr((e as Error).message);
      } finally {
        if (!cancelled) setResolvingSrc(false);
      }
    })();
    return () => {
      cancelled = true;
      resolved?.revoke();
    };
  }, [activeSourceUrl, activeSourceType, activeSourcePath]);

  async function onApplyPlanEdit(fi: number, recipe: EditRecipe) {
    if (!clientId) return;
    const fl = form.floors[fi];
    const fp = fl?.floorplan;
    if (!fp) return;
    const src = sourceRefFor(fp); // ALWAYS the pristine source (never the bake)
    if (!resolvedSrc) {
      onMsg('Still preparing the image — try again in a moment.');
      return;
    }
    setBaking(true);
    setUploadingFloor(fi);
    try {
      // Reuse the already-resolved raster (PDF page rasterised to canvas, or a
      // CORS-safe blob-loaded <img>). bake mime follows the resolved source.
      const name = derivedFileName(
        src.storagePath.split('/').pop() || 'floorplan',
        resolvedSrc.bakeMime,
      );
      const baked = await bakeImage(
        resolvedSrc.image,
        resolvedSrc.width,
        resolvedSrc.height,
        recipe,
        name,
        resolvedSrc.bakeMime,
      );
      const derived = await uploadDerivedFloorPlan(
        clientId,
        fl.id,
        baked.file,
        baked.width,
        baked.height,
      );
      // Layer original + recipe onto the derived FloorPlan (additive, flat).
      const edited: FloorPlan = {
        ...derived,
        ...editDocBody(src as OriginalImageRef, recipe),
      } as FloorPlan;
      const hadTrace =
        (fl.boundary && fl.boundary.length > 0) ||
        (fl.doors && fl.doors.length > 0) ||
        (fl.fixtures && fl.fixtures.length > 0);
      // New pixel frame → the old boundary/marks no longer align. Reset them.
      patchFloor(fi, {
        floorplan: edited,
        boundary: null,
        doors: [],
        entrances: [],
        main_entry_label: '',
        fixtures: [],
      });
      setEditFloorIdx(null);
      onMsg(
        hadTrace
          ? 'Edited image saved. The image frame changed — re-trace the outline, then save the floor.'
          : 'Edited image saved. Trace the outline, then save the floor.',
      );
    } catch (e) {
      onMsg(`Could not apply the edit: ${(e as Error).message}`);
    } finally {
      setBaking(false);
      setUploadingFloor(null);
    }
  }

  /* ── door markers (click-on-wall → door rectangle → covered padas DERIVED) ──
   *  The form's floor.doors[] (each {edge_index, parametric_t, width, type,
   *  name?, principal}) is the source of truth; floor.entrances[] (one per
   *  covered pada) + main_entry_label are DERIVED from it. We project doors to
   *  EntranceMark[] on demand (marksFromFloor, deriving centre+padas off the
   *  grid), apply a pure op, then write the derived
   *  {doors, entrances, main_entry_label} patch back. One owner (the form), so
   *  it round-trips through saveFloors. */
  function gridFor(boundary: Pt[] | null | undefined, north: number | null): GridResult | null {
    if (!isValidBoundary(boundary)) return null;
    try {
      return computeGrid({ boundary, rotation: north ?? 0 });
    } catch {
      return null;
    }
  }
  function marksFor(fl: FloorInput): EntranceMark[] {
    return marksFromFloor(fl, gridFor(fl.boundary, northForGrid), fl.boundary);
  }
  // AD-013 — the gridId FK to stamp onto marks placed in MARKING-ONLY mode: the
  // property grid the staff are marking on. null off the project mount / no grid.
  const markingGridId = hideGridTool && propGrid ? propGrid.gridId : null;
  function applyMarks(fi: number, marks: EntranceMark[]) {
    const patch = marksToFloorPatch(marks);
    // stamp the property grid FK onto every door (AD-013 — the engagement mark
    // records WHICH property grid it was placed on, so the report can resolve it).
    if (markingGridId) {
      patch.doors = patch.doors.map((d) => ({ ...d, gridId: markingGridId }));
    }
    patchFloor(fi, patch);
  }
  function onAddEntrance(fi: number, click: Pt) {
    pushUndoSnapshot();
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyMarks(fi, addMark(marksFor(fl), grid, fl.boundary, click));
  }
  /** Place an INTERNAL door at a free interior point (off a staircase / lift
   *  lobby) — pada read by direction from the centroid. */
  function onAddInternalEntrance(fi: number, click: Pt) {
    pushUndoSnapshot();
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyMarks(fi, addInternalMark(marksFor(fl), grid, click));
  }
  function onMoveEntrance(fi: number, id: string, drag: Pt) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    const marks = marksFor(fl);
    const m = marks.find((x) => x.id === id);
    // an internal door moves FREELY; a perimeter door slides along its wall.
    applyMarks(
      fi,
      m?.placement === 'internal'
        ? moveInternalMark(marks, id, drag, grid)
        : moveMark(marks, id, drag, grid, fl.boundary),
    );
  }
  function onSetEntranceEnd(
    fi: number,
    id: string,
    which: 'start' | 'end',
    drag: Pt,
  ) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    const marks = marksFor(fl);
    const m = marks.find((x) => x.id === id);
    // an internal door's end grip moves FREELY (rotate / resize); a perimeter
    // door's end grip rides the wall (re-snapped, span rebuilt).
    applyMarks(
      fi,
      m?.placement === 'internal'
        ? setInternalEnd(marks, id, which, drag, grid)
        : setMarkEnd(marks, id, which, drag, grid, fl.boundary),
    );
  }
  function onRemoveEntrance(fi: number, id: string) {
    pushUndoSnapshot();
    applyMarks(fi, removeMark(marksFor(form.floors[fi]), id));
  }
  function onSetEntranceType(fi: number, id: string, type: string) {
    applyMarks(fi, setMarkType(marksFor(form.floors[fi]), id, type));
  }
  function onSetEntranceName(fi: number, id: string, name: string) {
    applyMarks(fi, setMarkName(marksFor(form.floors[fi]), id, name));
  }
  function onSetPrincipal(fi: number, id: string) {
    applyMarks(fi, setPrincipal(marksFor(form.floors[fi]), id));
  }

  /* ── AUO markers (click inside the footprint → 16-zone DERIVED via the grid) ──
   *  Same derive-on-render / one-owner pattern as the door markers: the form's
   *  floor.fixtures[] is the source of truth; we project to AuoMark[] on demand
   *  (auoMarksFromFloor, deriving zone off the grid), apply a pure op, then write
   *  the fixtures patch back through saveFloors. */
  function auoMarksFor(fl: FloorInput): AuoMark[] {
    return auoMarksFromFloor(fl, gridFor(fl.boundary, northForGrid));
  }
  function applyAuoMarks(fi: number, marks: AuoMark[]) {
    let fixtures = marksToFixtures(marks);
    // stamp the property grid FK onto every AUO fixture (AD-013).
    if (markingGridId) {
      fixtures = fixtures.map((fx) => ({ ...fx, gridId: markingGridId }));
    }
    patchFloor(fi, { fixtures });
  }
  function onAddAuo(fi: number, click: Pt) {
    pushUndoSnapshot();
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, addAuo(auoMarksFor(fl), grid, click));
  }
  function onMoveAuo(fi: number, id: string, drag: Pt) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, moveAuo(auoMarksFor(fl), id, drag, grid));
  }
  function onResizeAuo(fi: number, id: string, size: { hw?: number; hh?: number; r?: number }) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, resizeAuo(auoMarksFor(fl), id, size, grid));
  }
  /** Grip-anchored resize: the OPPOSITE corner stays pinned (standard bounding-box
   *  resize), not a centre-stretch. `gripCorner` = which corner is dragged. */
  function onResizeAuoAnchored(fi: number, id: string, gripCorner: number, drag: Pt) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, resizeAuoAnchored(auoMarksFor(fl), id, gripCorner, drag, grid));
  }
  /** Re-open a confirmed/rejected AUO for editing (status → proposed, suggestions
   *  kept). The "Edit" affordance on the confirmed card / marker. */
  function onReopenAuo(fi: number, id: string) {
    applyAuoMarks(fi, reopenAuo(auoMarksFor(form.floors[fi]), id));
  }
  function onRotateAuo(fi: number, id: string, deg: number) {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, rotateAuo(auoMarksFor(fl), id, deg, grid));
  }
  function onSetAuoShape(fi: number, id: string, kind: 'rect' | 'circle') {
    const fl = form.floors[fi];
    const grid = gridFor(fl.boundary, northForGrid);
    applyAuoMarks(fi, setAuoShape(auoMarksFor(fl), id, kind, grid));
  }
  function onRemoveAuo(fi: number, id: string) {
    pushUndoSnapshot();
    applyAuoMarks(fi, removeAuo(auoMarksFor(form.floors[fi]), id));
  }

  /* ── eval-canvas MARKUP ops (annotations — NOT in the numbered AUO list) ──
   *  Persisted on floor 0 (markups ride INSIDE the floor object → saveFloors). */
  function markupsFor(fl: FloorInput | undefined): EvalMarkup[] {
    return Array.isArray(fl?.markups) ? (fl!.markups as EvalMarkup[]) : [];
  }
  function applyMarkups(fi: number, list: EvalMarkup[]) {
    patchFloor(fi, { markups: list });
  }
  function onAddMarkup(m: EvalMarkup) {
    pushUndoSnapshot();
    applyMarkups(0, addMarkup(markupsFor(form.floors[0]), m));
    setSelectedMarkupId(m.id);
  }
  function onMoveMarkup(id: string, drag: Pt) {
    applyMarkups(0, moveMarkup(markupsFor(form.floors[0]), id, drag));
  }
  function onResizeMarkup(id: string, drag: Pt) {
    applyMarkups(0, resizeMarkup(markupsFor(form.floors[0]), id, drag));
  }
  function onRemoveMarkup(id: string) {
    pushUndoSnapshot();
    applyMarkups(0, removeMarkup(markupsFor(form.floors[0]), id));
    setSelectedMarkupId((s) => (s === id ? null : s));
  }
  // markup inline-edit (right-pane Markups list) — set the free note / custom
  // label. Rides the same patchFloor({ markups }) → saveFloors path as add/move,
  // so an edited note/label round-trips through save → reload.
  function onSetMarkupNote(id: string, note: string) {
    applyMarkups(0, setMarkupNote(markupsFor(form.floors[0]), id, note));
  }
  function onSetMarkupLabel(id: string, label: string) {
    applyMarkups(0, setMarkupLabel(markupsFor(form.floors[0]), id, label));
  }
  function onSetAuoName(fi: number, id: string, name: string) {
    applyAuoMarks(fi, setAuoName(auoMarksFor(form.floors[fi]), id, name));
  }
  function onSetAuoType(fi: number, id: string, type: string) {
    applyAuoMarks(fi, setAuoType(auoMarksFor(form.floors[fi]), id, type));
  }
  // A palette pick sets name AND its derived type in ONE patch — two separate
  // setters would each read the stale closure `form` and the second would clobber
  // the first (the name was lost on pick).
  function onSetAuoNameType(fi: number, id: string, name: string, type: string) {
    const marks = auoMarksFor(form.floors[fi]);
    applyAuoMarks(fi, setAuoType(setAuoName(marks, id, name), id, type));
  }
  function onSetAuoZones(fi: number, id: string, zones: string[]) {
    applyAuoMarks(fi, setAuoZones(auoMarksFor(form.floors[fi]), id, zones));
  }

  /** ⭐ OVERRIDE LOOP — the owner CORRECTS one (AUO × zone) disposition/effect/
   *  remedy. Persists to `vastu_auo_overrides` (admin-gated rule) and applies the
   *  correction to the mark's suggestion IMMEDIATELY so the card reflects it
   *  without a callable round-trip. The Stage-5 callable merges this override over
   *  the canonical table on the NEXT "Ask engine" (and the compose path later). */
  async function onCorrectAuoDisposition(
    fi: number,
    id: string,
    zone: string,
    patch: {
      disposition: OverrideDisposition;
      effect: string;
      management?: string | null;
      correctiveColour?: string | null;
      correctiveMetal?: string | null;
      priorSource?: string | null;
    },
    // "ask me each time" (Silas §3.1) — the admin picks the write scope on save.
    // Defaults to 'global' so the legacy single-Save path is byte-unchanged.
    scope: 'project' | 'global' = 'global',
  ) {
    // defence-in-depth: the editor is admin-gated in the UI AND the Firestore rule
    // is admin-only; this client guard is the third layer.
    if (!isAdmin) {
      onMsg('Only an admin/owner can correct a disposition.');
      return;
    }
    if (scope === 'project' && !projectId) {
      onMsg('No project in scope — open this from a project to save a project-only correction.');
      return;
    }
    const marks = auoMarksFor(form.floors[fi]);
    const mark = marks.find((x) => x.id === id);
    const z = (zone || '').trim().toUpperCase();
    // the canonical key the override doc id is built from — prefer the engine's
    // resolved `normalised`; fall back to the marked name (the callable resolves
    // aliases too, but the doc id must match the canonical key it reads).
    const canonical =
      (mark?.suggestionsByZone?.[z]?.normalised || '').trim() || (mark?.name || '').trim();
    if (!canonical) {
      onMsg('Name the AUO before correcting its disposition.');
      return;
    }
    const rec = buildOverrideRecord({
      auoKey: canonical,
      zone: z,
      disposition: patch.disposition,
      effect: patch.effect,
      management: patch.management ?? null,
      correctiveColour: patch.correctiveColour ?? null,
      correctiveMetal: patch.correctiveMetal ?? null,
      editedBy: actor.uid || actor.name || null,
      priorSource: patch.priorSource ?? mark?.suggestionsByZone?.[z]?.source ?? null,
    });
    try {
      // project → per-project subcollection (the global table is untouched);
      // global → the existing reference-library write, exactly as before.
      if (scope === 'project') await saveProjectAuoOverride(rec, projectId!);
      else await saveAuoOverride(rec);
    } catch (err) {
      onMsg(`Could not save the correction: ${(err as Error)?.message || 'permission denied'}`);
      return;
    }
    // reflect the correction on the card NOW (the merge is authoritative server-side),
    // carrying the scope so the card labels "this project" vs "everywhere".
    const next = applyAuoSuggestion(
      auoMarksFor(form.floors[fi]),
      id,
      overrideToSuggestion({ ...rec, override_scope: scope }),
    );
    applyAuoMarks(fi, next);
    onMsg(
      scope === 'project'
        ? `Correction saved for THIS project — ${canonical} × ${z} → ${patch.disposition}.`
        : `Correction saved — ${canonical} × ${z} → ${patch.disposition}. Used everywhere from now on.`,
    );
  }

  /** Fetch the engine's disposition reasoning for a floor's marked AUOs and
   *  attach each suggestion to its mark (index-aligned: the callable preserves
   *  order, dropping only rows with no name/zone — we re-derive marks then match
   *  by name+zone to be safe). */
  async function onSuggestAuo(fi: number) {
    if (!clientId) return;
    const fl = form.floors[fi];
    const marks = auoMarksFor(fl);
    const reviewable = marks.filter((m) => (m.zones || []).length > 0 && !!(m.name || '').trim());
    if (reviewable.length === 0) {
      onMsg('Mark at least one AUO (with a name) before asking the engine.');
      return;
    }
    setAuoBusyFloor(fi);
    try {
      // Send each AUO's FULL zone set — the callable returns one disposition row
      // PER (mark × covered zone).
      const res = await suggestAuoFixtures({
        clientId,
        floorId: fl.id,
        marks: reviewable.map((m) => ({ name: m.name, zones: m.zones, type: m.type })),
      });
      // Group the per-zone result rows back by (name) and attach a zone→suggestion
      // map to each matching mark. A mark gets the suggestions for all of ITS zones.
      let next = auoMarksFor(form.floors[fi]);
      for (const mark of next) {
        const myName = (mark.name || '').trim().toLowerCase();
        if (!myName || (mark.zones || []).length === 0) continue;
        const byZone: Record<string, AuoSuggestion> = {};
        for (const z of mark.zones) {
          const fx = res.fixtures.find(
            (f) => (f.name || '').trim().toLowerCase() === myName && f.zone === z,
          );
          if (fx) byZone[z] = toSuggestion(fx);
        }
        if (Object.keys(byZone).length) {
          next = applyAuoSuggestions(next, mark.id, byZone);
        }
      }
      applyAuoMarks(fi, next);
      const unrec = res.fixtures.filter((f) => !f.recognised).length;
      const rows = res.fixtures.length;
      onMsg(
        `Engine reviewed ${reviewable.length} AUO${reviewable.length === 1 ? '' : 's'} across ${rows} zone${rows === 1 ? '' : 's'}.${
          unrec ? ` ${unrec} not recognised — rename or remove.` : ''
        }`,
      );
    } catch (e) {
      onMsg(`Could not reach the engine: ${(e as Error).message}`);
    } finally {
      setAuoBusyFloor(null);
    }
  }

  /** The decision-log disposition for a multi-zone mark = its WORST cell (highest
   *  severity) so the training signal records the most-critical placement; falls
   *  back to the first-zone read. */
  function primaryDecision(m: AuoMark): { zone: string; disposition: string | null; severity: number } {
    const byZone = m.suggestionsByZone || {};
    let best: { zone: string; disposition: string | null; severity: number } | null = null;
    for (const z of m.zones || []) {
      const s = byZone[z];
      if (!s) continue;
      if (!best || s.severity > best.severity) {
        best = { zone: z, disposition: s.disposition, severity: s.severity };
      }
    }
    return best ?? { zone: m.zone || (m.zones || [])[0] || '', disposition: null, severity: 0 };
  }

  /** Confirm an AUO → it reaches compose. Logs the decision (training signal). */
  function onConfirmAuo(fi: number, id: string) {
    const fl = form.floors[fi];
    const marks = auoMarksFor(fl);
    const m = marks.find((x) => x.id === id);
    applyAuoMarks(fi, confirmAuo(marks, id));
    if (clientId && m) {
      const dec = primaryDecision(m);
      void logAuoDecision(
        clientId,
        buildDecisionRecord({
          floorId: fl.id,
          name: m.name,
          zone: dec.zone,
          disposition: dec.disposition,
          severity: dec.severity,
          action: 'confirm',
          staffUid: actor.uid,
        }),
      );
    }
  }

  /** Reject an AUO → logged, NOT compiled. Logs the decision (training signal). */
  function onRejectAuo(fi: number, id: string) {
    const fl = form.floors[fi];
    const marks = auoMarksFor(fl);
    const m = marks.find((x) => x.id === id);
    applyAuoMarks(fi, rejectAuo(marks, id, actor.name || actor.uid || 'staff'));
    if (clientId && m) {
      const dec = primaryDecision(m);
      void logAuoDecision(
        clientId,
        buildDecisionRecord({
          floorId: fl.id,
          name: m.name,
          zone: dec.zone,
          disposition: dec.disposition,
          severity: dec.severity,
          action: 'reject',
          staffUid: actor.uid,
        }),
      );
    }
  }

  async function onSaveFloors() {
    if (!clientId) return;
    setSavingFloors(true);
    try {
      // Commit the active property's working buffer (form.floors + north) back
      // into props_, then persist the WHOLE candidate-property array. A single-
      // property project writes a 1-element array (+ answers.floors mirror for
      // the L2B/single-property read path). The engagement-level north is still
      // written to the client profile (per-property north is on each property).
      const committed = commitActiveIntoProps();
      setProps_(committed);
      await saveProperties(
        scopeFor(clientId),
        { north_rotation: form.north_rotation, properties: committed },
        { uid: actor.uid, via: 'staff' },
        { recommendedPropertyId: recommendedPropId },
        intakeTier,
      );
      setFloorsDirty(false);
      setFloorsSavedAt(new Date());
      // Wording (2026-07-01): this Save commits the whole per-property
      // EVALUATION (boundary + entrances + AUOs + markups + recommendation), not
      // just floors — reflect that on the evaluation surface.
      // One Save commits the whole per-property evaluation for every candidate
      // at once; showing a count ("2 …saved") reads as confusing when the user
      // is focused on one property — so keep it simple and count-free.
      onMsg('Evaluation saved.');
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSavingFloors(false);
    }
  }

  // ── Phase-1 evaluation-workspace bridge (spec §3.1). ONLY in workspace mode
  // (enableZoom) — inert for every ProjectDetail / L1AFlow mount. The workspace's
  // sticky footer is OUTSIDE this panel, so its green "Save evaluation" reaches
  // the panel's existing `onSaveFloors` via a decoupled window event; the panel
  // emits its dirty/saved state back so the workspace's autosave pill stays in
  // sync. No new save path — the same `onSaveFloors` → `saveProperties`.
  // Keep the latest save closure in a ref so the listener binds ONCE (scoped to
  // [enableZoom]) yet always commits the current buffer (Vera NIT #6 — no
  // per-render add/removeEventListener churn).
  const onSaveFloorsRef = useRef(onSaveFloors);
  onSaveFloorsRef.current = onSaveFloors;
  useEffect(() => {
    if (!enableZoom) return;
    const onRequestSave = () => {
      void onSaveFloorsRef.current();
    };
    window.addEventListener('evalws:save', onRequestSave);
    return () => window.removeEventListener('evalws:save', onRequestSave);
  }, [enableZoom]);
  useEffect(() => {
    if (!enableZoom) return;
    window.dispatchEvent(
      new CustomEvent('evalws:dirty-change', {
        detail: { dirty: floorsDirty, savedAt: floorsSavedAt ? floorsSavedAt.toISOString() : null },
      }),
    );
  }, [enableZoom, floorsDirty, floorsSavedAt]);

  /* ────────────────────────────────────────────────────────────────────────
   * EVAL WORKSPACE BRIDGE (build spec Phase B). In the eval workspace mount
   * (enableZoom) the marking happens on floor 0 (the primary surface). We EMIT
   * the unified disposition (doors first, then AUOs) to the right-pane tabs over
   * `evalDispositionBridge`, and accept select-mark requests back. The panel
   * stays the single source of truth — this is a read-out + a selection echo,
   * never a second store. Off the eval mount these effects no-op (byte-safe).
   * ──────────────────────────────────────────────────────────────────────── */
  // pure label maps for the door type vocab (Main/Secondary/Internal/Stair/Balcony).
  const evalFloor0 = enableZoom ? form.floors[0] : undefined;
  const evalDispoItems = useMemo<DispoItem[]>(() => {
    if (!enableZoom || !evalFloor0) return [];
    const DOOR_LABEL: Record<string, string> = {
      main_door: 'Main door',
      secondary_door: 'Secondary door',
      back_door: 'Back door',
      servant_entry: 'Servant entry',
      balcony_opening: 'Balcony opening',
      staircase_entry: 'Stair door',
      internal_entry: 'Internal door',
      lift_entry: 'Lift door',
      balcony_entry: 'Balcony entry',
    };
    const padaText = (padas: string[]): string =>
      padas.length === 0
        ? '?'
        : padas.length === 1
          ? padas[0]
          : `${padas[0]}–${padas[padas.length - 1]}`;
    const doors = marksFor(evalFloor0);
    const auos = auoMarksFor(evalFloor0);
    const items: DispoItem[] = [];
    let n = 1;
    // doors take the first numbers (decision 13)
    for (const d of doors) {
      const label = DOOR_LABEL[d.type] || 'Door';
      items.push({
        key: d.id,
        kind: 'door',
        seqNo: n++,
        name: label,
        typeLabel: label,
        padaOrZone: padaText(d.padas),
        principal: d.isPrincipal,
        rawType: d.type,
        // the individual covered padas → the door card resolves one entrance
        // reading per pada (client-side lookup, entranceCodes32).
        padas: d.padas,
      });
    }
    // AUOs continue the same sequence (decision 13)
    for (const a of auos) {
      // one engine row per covered zone, in the shape's zone order — the
      // preview's "name → zone → engine ruling" contract (avbp_auo_suggest).
      const suggestions = a.suggestionsByZone
        ? (a.zones && a.zones.length ? a.zones : Object.keys(a.suggestionsByZone))
            .map((z) => a.suggestionsByZone?.[z])
            .filter((s): s is NonNullable<typeof s> => !!s)
            .map((s) => ({
              zone: s.zone || '',
              disposition: s.disposition,
              severity: s.severity,
              severityLabel: s.severity_label,
              reason: s.reason,
              recognised: s.recognised,
              // carried so the admin disposition editor can start from the current
              // ruling + label a project vs global correction (Edit Disposition).
              normalised: s.normalised ?? null,
              effect: s.effect ?? null,
              management: s.management ?? null,
              // the owner's corrective colour + metal — rendered as the swatch in the
              // workspace list, matching the inline staff card (AUO-list unification).
              corrective_colour: s.corrective_colour ?? null,
              corrective_metal: s.corrective_metal ?? null,
              source: s.source ?? null,
              overrideScope: s.override_scope ?? null,
            }))
        : undefined;
      items.push({
        key: a.id,
        kind: 'auo',
        seqNo: n++,
        name: a.name || 'AUO',
        typeLabel: a.type,
        padaOrZone: (a.zones && a.zones.length ? a.zones.join(', ') : a.zone) || '—',
        status: a.status,
        auoName: a.name,
        zones: a.zones,
        suggestions: suggestions && suggestions.length ? suggestions : undefined,
      });
    }
    return items;
    // form.floors drives re-derivation on every mark mutation; northForGrid moves the grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableZoom, evalFloor0, form.floors, northForGrid]);

  // emit the door+AUO disposition whenever it changes.
  useEffect(() => {
    if (!enableZoom) return;
    emitMarks(evalDispoItems);
  }, [enableZoom, evalDispoItems]);

  // emit the AUO name palette so the right-pane naming card runs the SAME
  // searchable picker the left list uses (re-emit if the palette finishes loading).
  useEffect(() => {
    if (!enableZoom) return;
    emitPalette(palette?.items ?? []);
  }, [enableZoom, palette]);

  // emit the session context (admin flag + project) so the right pane can gate the
  // ADMIN-only disposition editor on the REAL session — never a hardcoded flag —
  // and enable the "this project only" save scope. Mirrors the Firestore admin rule.
  useEffect(() => {
    if (!enableZoom) return;
    emitContext({ isAdmin, projectId: projectId ?? null });
  }, [enableZoom, isAdmin, projectId]);

  // WORKSPACE PORTAL: in the dedicated eval workspace the map lives in the LEFT
  // pane and the review card must appear in the RIGHT pane's "AUO disposition"
  // tab. Rather than re-implement the card (drift + missing controls), we PORTAL
  // the real AuoReviewPanel subtree into the right pane's target node. This holds
  // that node (published by ReferenceMediaPane); null when the tab isn't active.
  const [auoPortalHost, setAuoPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!workspaceMode) return;
    return subscribeAuoPortalHost(setAuoPortalHost);
  }, [workspaceMode]);

  // the MARKUP disposition, folded into the ONE unified sequence (owner-confirmed):
  // entrances → AUOs → markups, unbroken. Seed each markup's seqNo at (door + AUO
  // count) + index — both the count and the markups read form.floors[0] in THIS
  // render, so the offset never drifts and the map pin == the row #. Shared by the
  // right-pane badge (via emitMarkups) AND the portalled UnifiedDispoList below.
  const evalMarkupItems = useMemo<UnifiedMarkup[]>(() => {
    if (!enableZoom) return [];
    const fl0 = form.floors[0];
    if (!fl0) return [];
    const offset = marksFor(fl0).length + auoMarksFor(fl0).length;
    // derive each markup's covered zone set off the SAME grid AUOs/doors use, so the
    // expand-detail can show which zones/padas the markup falls over (no disposition).
    const markupGrid = gridFor(fl0.boundary, northForGrid);
    return markupsFor(fl0).map((m, i) => ({
      id: m.id,
      type: m.type,
      label: m.label || MARKUP_LABEL[m.type],
      note: m.note,
      zones: zonesForMarkup(markupGrid, m),
      seqNo: offset + i + 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableZoom, form.floors, northForGrid]);

  // emit the markup disposition whenever it changes (right-pane badge total).
  useEffect(() => {
    if (!enableZoom) return;
    emitMarkups(evalMarkupItems);
  }, [enableZoom, evalMarkupItems]);

  // echo the current selection so the right-pane lists highlight the active row.
  // Markup ids (mk…) and door/AUO ids (en…/au…) are distinct namespaces, so a
  // single channel highlights the correct list.
  useEffect(() => {
    if (!enableZoom) return;
    emitMarkSelected(selectedMarkupId ?? selectedAuoId);
  }, [enableZoom, selectedAuoId, selectedMarkupId]);

  // accept "select this marker" from a disposition row → select it (AUO marks
  // also highlight on the canvas via selectedAuoId; door rows highlight in-list).
  useEffect(() => {
    if (!enableZoom) return;
    return subscribeSelectMark((key) => setSelectedAuoId(key));
  }, [enableZoom]);

  // accept edit/delete/confirm/reject from the RIGHT-pane disposition rows (#4–#6)
  // and route them to the panel's existing mark ops on floor 0. The key resolves
  // to a door or an AUO; doors → entrance ops, AUOs → AUO confirm-loop ops.
  useEffect(() => {
    if (!enableZoom) return;
    return subscribeMarkAction((a) => {
      const fl = form.floors[0];
      if (!fl) return;
      const isDoor = marksFor(fl).some((d) => d.id === a.key);
      const isAuo = !isDoor && auoMarksFor(fl).some((x) => x.id === a.key);
      // a DISCRETE single-click edit (not a per-keystroke or read-only op) is
      // snapshotted for undo BEFORE it runs. The delete handlers (onRemove*)
      // snapshot themselves, so delete is NOT double-snapshotted here.
      const DISCRETE_EDITS = new Set([
        'set-door-type', 'set-principal', 'confirm', 'reject', 'reopen', 'set-auo-name-type',
        'set-auo-zones',
      ]);
      if (DISCRETE_EDITS.has(a.action)) pushUndoSnapshot();
      if (isDoor) {
        if (a.action === 'delete') onRemoveEntrance(0, a.key);
        else if (a.action === 'set-door-type' && a.value) onSetEntranceType(0, a.key, a.value);
        else if (a.action === 'set-principal') onSetPrincipal(0, a.key);
      } else if (isAuo) {
        if (a.action === 'delete') onRemoveAuo(0, a.key);
        else if (a.action === 'confirm') onConfirmAuo(0, a.key);
        else if (a.action === 'reject') onRejectAuo(0, a.key);
        else if (a.action === 'reopen') onReopenAuo(0, a.key);
        else if (a.action === 'set-auo-name') onSetAuoName(0, a.key, a.value || '');
        // a palette pick sets name + derived type ATOMICALLY (one patch) so the
        // second setter never clobbers the first off a stale closure.
        else if (a.action === 'set-auo-name-type') onSetAuoNameType(0, a.key, a.value || '', a.auoType || '');
        // override the grid-derived zone set (the "edit zones" affordance) — routes
        // to the SAME pure op the inline staff card uses (onSetAuoZones).
        else if (a.action === 'set-auo-zones' && a.zones) onSetAuoZones(0, a.key, a.zones);
        // a free-typed custom name with no palette match → flow it to the pending
        // collection for admin/Pitra promotion (best-effort, never blocks marking).
        else if (a.action === 'record-pending-auo') {
          if (palette && a.value) {
            void recordPendingAuo(paletteFileFor(palette.file), a.value, { uid: actor.uid, name: actor.name });
          }
        }
        // staff press "Ask engine" only AFTER the name is committed to form state
        // (a separate user action), so onSuggestAuo reads the fresh name — no
        // stale-closure race from chaining set-name → suggest in one tick.
        else if (a.action === 'suggest') onSuggestAuo(0);
        // ADMIN-only: correct one (AUO × zone) ruling and save it to the reference
        // library at the chosen scope. onCorrectAuoDisposition re-checks isAdmin
        // (defence-in-depth) and dispatches to the project vs global writer.
        else if (a.action === 'correct-disposition' && a.zone) {
          void onCorrectAuoDisposition(
            0,
            a.key,
            a.zone,
            {
              disposition: (a.disposition || 'neutral') as OverrideDisposition,
              effect: a.effect || '',
              management: a.management ?? null,
            },
            a.scope === 'project' ? 'project' : 'global',
          );
        }
      } else {
        // a markup row — delete or inline-edit the note / custom label.
        if (a.action === 'delete') onRemoveMarkup(a.key);
        else if (a.action === 'set-markup-note') onSetMarkupNote(a.key, a.value || '');
        else if (a.action === 'set-markup-label') onSetMarkupLabel(a.key, a.value || '');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableZoom, form.floors, palette]);

  // In the dedicated workspace the floor-plan canvas is the bare hero — drop the
  // "Floors & grid" Box chrome (title/intro/add-floor); elsewhere keep the Box.
  const FloorsWrap = workspaceMode ? MarkingWrap : Box;

  // "Push to registry" removed (2026-06-28, Gap #6). Property IS the registry;
  // report immutability is via versioned PDFs (L1A_v{n}) — no manual push needed.
  // The boundary/grid traced on the Property page is the single source of truth the
  // report composer reads; nothing here depends on a push having happened.

  /* ── site location ── */
  const parsed = useMemo(
    () => (mapsUrl.trim() ? parseGoogleMapsUrl(mapsUrl) : null),
    [mapsUrl],
  );
  const shortLink = useMemo(() => isShortGoogleLink(mapsUrl), [mapsUrl]);
  // SINGLE SOURCE OF TRUTH (16th dispatch): the Coordinates FIELD always holds
  // the effective value. When a Google link is pasted and yields coords, mirror
  // the canonical "lat, long" INTO the field so the owner can SELECT + COPY it
  // (the old bug: the field showed only its placeholder + a helper line while the
  // real value was uncopyable). We only WRITE when the extracted value differs
  // from what the field already shows, so a subsequent MANUAL edit is never
  // clobbered (the link is unchanged → parsed is stable → no re-write). The
  // field stays freely editable; a manual override updates `coords` directly.
  const linkExtract = useMemo(
    () => (parsed ? formatCoordPair(parsed.lat, parsed.lon) : ''),
    [parsed],
  );
  useEffect(() => {
    if (linkExtract && linkExtract !== coords) setCoords(linkExtract);
    // depend on linkExtract ONLY: a manual edit changes `coords` but NOT
    // linkExtract (the link is the same), so it does not re-fire and overwrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkExtract]);
  // The effective coords always come from the field now (a pasted link has been
  // mirrored into it above); `source` provenance still notes the link.
  const liveSite = useMemo(
    () =>
      buildSiteLocation({
        coordPair: coords,
        googleUrl: mapsUrl,
      }),
    [coords, mapsUrl],
  );
  // Gentle inline validation for the combined field: non-empty but unparseable
  // → show a hint. The field is always the source now, so validate it directly.
  const coordsInvalid = useMemo(
    () => coords.trim() !== '' && parseCoordPair(coords) == null,
    [coords],
  );
  const launch = hasValidCoords(liveSite)
    ? mapLaunchUrls(liveSite.lat, liveSite.lon)
    : null;

  async function onSaveSite() {
    if (!clientId) return;
    setSavingSite(true);
    try {
      const built = buildSiteLocation({
        coordPair: coords,
        googleUrl: mapsUrl,
      });
      await saveSiteLocation(scopeFor(clientId), built, { uid: actor.uid, via: 'staff' }, intakeTier);
      // Reflect the canonical "lat, long" string back into the field so it
      // round-trips with Google (a DMS / messy input is normalised on save).
      setCoords(formatCoordPair(built.lat, built.lon));
      setMapsUrl(built.google_maps_url);
      onMsg('Site location saved.');
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSavingSite(false);
    }
  }

  async function onSaveReportContext() {
    if (!clientId) return;
    setSavingReportCtx(true);
    try {
      await saveReportContext(scopeFor(clientId), reportCtx, { uid: actor.uid, via: 'staff' }, intakeTier);
      onMsg('Report context saved.');
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSavingReportCtx(false);
    }
  }

  /* ── Room-allocation override (curation) row mutations ── */
  function addRoomOverride() {
    setReportCtx((c) => ({
      ...c,
      room_overrides: [...c.room_overrides, { member: '', zone: '', floorId: '' }],
    }));
  }
  function patchRoomOverride(i: number, patch: Partial<RoomOverrideRow>) {
    setReportCtx((c) => {
      const next = c.room_overrides.slice();
      next[i] = { ...next[i], ...patch };
      return { ...c, room_overrides: next };
    });
  }
  function removeRoomOverride(i: number) {
    setReportCtx((c) => ({
      ...c,
      room_overrides: c.room_overrides.filter((_, j) => j !== i),
    }));
  }

  /** Compact one-line summary for a COLLAPSED floor row (item 6):
   *  name · has-plan? · #doors · grid ✓. */
  function floorSummary(fl: FloorInput): {
    hasPlan: boolean;
    doorCount: number;
    gridReady: boolean;
  } {
    const hasPlan = !!(
      fl.floorplan &&
      (fl.floorplan.downloadURL || fl.floorplan.storagePath)
    );
    const doorCount = (fl.doors || []).length;
    const gridReady = isValidBoundary(fl.boundary);
    return { hasPlan, doorCount, gridReady };
  }

  if (!clientId) {
    return (
      <Box
        title="Technical intake (staff)"
        note="North · floors · entrances · site location"
      >
        <EmptyNote title="Link a client first">
          This project has no linked client. Link one (Edit project) before
          adding the technical intake — north, floors and site location are
          stored on the client record.
        </EmptyNote>
      </Box>
    );
  }

  // The floor whose plan editor is open (if any) — edit FROM the pristine
  // source, seeded with the recipe already applied so re-opening shows it.
  const editFloor = editFloorIdx != null ? form.floors[editFloorIdx] : null;
  const editFloorSrc =
    editFloor?.floorplan ? sourceRefFor(editFloor.floorplan) : null;

  return (
    <>
      {/* ══ Crop & rotate editors (modal). Floor-plan editor bakes a derived
          tracing/report surface; the north-screenshot editor crops out the
          Google-Earth tool panel. Both edit the SOURCE non-destructively. ══ */}
      {editFloor && editFloorSrc && (
        <CropRotateEditor
          // Preview the RESOLVED raster (a PDF page is rasterised, a raster is
          // blob-loaded so the bake never CORS-taints) — never the raw URL.
          imageUrl={resolvedSrc?.previewUrl ?? ''}
          // Dims come from the resolved raster (a PDF's stored dims are 0/0).
          srcWidth={resolvedSrc?.width ?? 0}
          srcHeight={resolvedSrc?.height ?? 0}
          initialRecipe={recipeFor(editFloor.floorplan as FloorPlan)}
          title={`Crop & rotate — ${editFloor.name || 'floor plan'}`}
          busy={baking}
          preparing={resolvingSrc}
          loadError={resolveErr}
          onApply={(recipe) => onApplyPlanEdit(editFloorIdx as number, recipe)}
          onCancel={() => setEditFloorIdx(null)}
        />
      )}
      {editNorthOpen && northShot?.download_url && (
        <CropRotateEditor
          imageUrl={resolvedSrc?.previewUrl ?? ''}
          srcWidth={resolvedSrc?.width ?? 0}
          srcHeight={resolvedSrc?.height ?? 0}
          title="Crop & rotate — north / location screenshot"
          busy={baking}
          preparing={resolvingSrc}
          loadError={resolveErr}
          onApply={onApplyNorthEdit}
          onCancel={() => setEditNorthOpen(false)}
        />
      )}
      {/* PDF PAGE PICKER (multi-page upload) — choose the page that holds the
          floor plan before it is rasterised + uploaded. */}
      {pagePick && (
        <PdfPagePicker
          fileName={pagePick.file.name}
          pageCount={pagePick.pageCount}
          pdfBytes={pagePick.bytes}
          busy={uploadingFloor === pagePick.floorIdx}
          onConfirm={onConfirmPdfPage}
          onCancel={() => setPagePick(null)}
        />
      )}

      {/* ══ (1) SITE & ORIENTATION — suppressed when floorsOnly=true, when linked
          properties exist, or when the mount says hideSiteOrientation (FIX D,
          owner bug 2026-07-03: on the L1A Evaluate mount this card writes
          engagement-level north/site the per-property grid never reads, and the
          propertyRefs gate alone misses stamp-lane projects like VP678). ══ */}
      {!floorsOnly && !workspaceMode && !hideSiteOrientation && linkedPropertyIds.length === 0 && <Box
        title="Site & orientation (staff)"
        note="Where the site is + which way it faces — the grid engine reads the north bearing"
      >
        {loading && (
          <p style={{ fontSize: 13, color: 'var(--ink-mute)' }}>Loading…</p>
        )}

        {/* ══ SITE LOCATION FIRST (owner reorder 2026-06-08, 16th dispatch).
            Order inside the card: (1) Paste Google Maps link → (2) Coordinates
            field → (3) Open-in buttons → divider → (4) True north + screenshot.
            Site location saves independently; the engine does not use coords. ══ */}
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: 'var(--ink)',
            margin: '2px 0 4px',
            letterSpacing: 0.2,
          }}
        >
          Site location
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
          Paste a Google link (or coordinates) — then open the site on a map in
          one click. The engine does not use these coordinates.
        </p>

        {/* (1) Paste a Google Maps link — FIRST. Auto-extracts the coords INTO
            the Coordinates field below. */}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Paste a Google Maps link</label>
          <input
            style={field}
            value={mapsUrl}
            onChange={(e) => setMapsUrl(e.target.value)}
            placeholder="Paste the full maps.google.com link"
            aria-label="Paste a Google Maps link"
          />
          {parsed && (
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              Extracted {parsed.lat}, {parsed.lon} (from the {parsed.via} in the
              link) — filled into the Coordinates field below.
            </p>
          )}
          {!parsed && shortLink && (
            <p style={{ fontSize: 11.5, color: 'var(--crit, #dc2626)', marginTop: 4 }}>
              This is a shortened link — open it, then paste the FULL link from
              the address bar (or enter the coordinates below).
            </p>
          )}
          {!parsed && !shortLink && mapsUrl.trim() !== '' && (
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              No coordinates found in that link — enter them in the field below.
            </p>
          )}
        </div>

        {/* (2) Coordinates field — SECOND. ONE combined "lat, long" box (decimal
            OR DMS; minus for S/W). Single source of truth: a pasted link mirrors
            its coords HERE (selectable + copyable), and a manual edit overrides.
            Round-trips with Google as "lat, long". */}
        <div>
          <label style={lbl}>Coordinates (latitude, longitude)</label>
          <input
            style={field}
            value={coords}
            onChange={(e) => setCoords(e.target.value)}
            placeholder={`22.7196, 75.8577`}
            aria-label="Coordinates as latitude, longitude (decimal or DMS)"
          />
          {coordsInvalid ? (
            <p style={{ fontSize: 11.5, color: 'var(--crit, #dc2626)', marginTop: 4 }}>
              Couldn’t read those coordinates. Use “lat, long” — decimal
              (e.g. 22.7196, 75.8577 · -33.8688, 151.2093) or DMS
              (e.g. 22°43'10.6"N 75°51'27.7"E).
            </p>
          ) : parsed ? (
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              Filled from the pasted link — select + copy it, or type over it to
              override.
            </p>
          ) : (
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              Paste “lat, long” straight from Google (decimal or DMS). Use a
              minus for south/west, e.g. -33.8688, 151.2093.
            </p>
          )}
        </div>

        {/* (3) Open-in buttons — THIRD. Google Maps + Google Earth only; the
            Apple open-in button was removed per the owner 2026-06-08 (the
            mapLaunchUrls helper still mints an apple url, just not rendered). */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '14px 0 4px',
          }}
        >
          {launch ? (
            <>
              <a href={launch.google} target="_blank" rel="noopener noreferrer">
                <Btn3 intent="ink" size="sm">
                  🗺 Google Maps
                </Btn3>
              </a>
              <a href={launch.earth} target="_blank" rel="noopener noreferrer">
                <Btn3 intent="ink" size="sm">
                  🌍 Google Earth
                </Btn3>
              </a>
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                {liveSite.lat}, {liveSite.lon}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
              Add coordinates above to enable the map buttons.
            </span>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <Btn3
            intent="grn"
            size="sm"
            onClick={onSaveSite}
            disabled={savingSite}
          >
            {savingSite ? 'Saving…' : 'Save site location'}
          </Btn3>
        </div>

        {/* ── divider between Site location and True north ── */}
        <div
          style={{
            height: 1,
            background: 'var(--rule)',
            margin: '18px 0 14px',
          }}
        />

        {/* ══ (4) TRUE NORTH — LAST (owner reorder 2026-06-08). Saves
            independently to profile.north_rotation. Plus the Google-Earth
            screenshot upload. ══ */}
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: 'var(--ink)',
            margin: '2px 0 8px',
            letterSpacing: 0.2,
          }}
        >
          True north
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '0 0 180px' }}>
            <label style={lbl}>True-north bearing (°)</label>
            <input
              style={field}
              value={form.north_rotation}
              onChange={(e) =>
                setForm((f) => ({ ...f, north_rotation: e.target.value }))
              }
              placeholder="e.g. 347"
              inputMode="decimal"
              aria-label="True-north bearing in degrees"
            />
          </div>
          <Btn3
            intent="grn"
            size="sm"
            onClick={onSaveNorth}
            disabled={savingNorth || northVal == null}
          >
            {savingNorth ? 'Saving…' : 'Save north'}
          </Btn3>
          {savedNorth != null && (
            <Tag kind="val" title="Stored on the client profile">
              saved · {savedNorth}°
            </Tag>
          )}
        </div>
        {northInvalid && (
          <p style={{ fontSize: 12, color: 'var(--crit, #dc2626)', marginTop: 6 }}>
            Enter a number between 0 and 360.
          </p>
        )}
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 8 }}>
          0° = grid aligned to true north. This rotates every zone of the
          analysis — confirm it from the surveyed site plan.
        </p>

        {/* ── True-north Google-Earth screenshot upload (16th dispatch). Measure
            the bearing in Google Earth, screenshot it, upload it for reference /
            the report. Reuses the floor-plan upload pattern (hidden input + a
            thumbnail + replace/remove). Durable ref on profile.north_screenshot. */}
        <div style={{ marginTop: 14 }}>
          <label style={lbl}>Google Earth north screenshot</label>
          <input
            ref={shotFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            aria-label="Upload Google Earth north screenshot"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadNorthShot(f);
              e.target.value = '';
            }}
          />
          {northShot && northShot.download_url ? (
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <a
                href={northShot.download_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the full screenshot"
              >
                <img
                  src={northShot.download_url}
                  alt="True-north Google Earth screenshot"
                  style={{
                    width: 120,
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--rule-strong)',
                    display: 'block',
                  }}
                />
              </a>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn3
                  intent="ink"
                  size="sm"
                  onClick={() => shotFileRef.current?.click()}
                  disabled={uploadingShot}
                >
                  {uploadingShot ? 'Uploading…' : 'Replace screenshot'}
                </Btn3>
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => setEditNorthOpen(true)}
                  disabled={uploadingShot}
                  title="Crop out the Measure tool panel / clutter (and rotate if needed)"
                >
                  Crop &amp; rotate
                </Btn3>
                <Btn3 intent="ghost" size="sm" onClick={onRemoveNorthShot}>
                  Remove
                </Btn3>
              </div>
            </div>
          ) : (
            <Btn3
              intent="ink"
              size="sm"
              onClick={() => shotFileRef.current?.click()}
              disabled={uploadingShot}
            >
              {uploadingShot ? 'Uploading…' : 'Upload Google Earth screenshot'}
            </Btn3>
          )}
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6 }}>
            Optional. Stored for reference and possible use in the report.
          </p>
        </div>
      </Box>}

      {/* ══ REPORT CONTEXT (staff) — suppressed when floorsOnly OR (L1A flow) when
            hideReportContext (the verdict moves to Step 5, the rest to the drawer) ══ */}
      {!floorsOnly && !hideReportContext && <Box
        title="Report context (staff)"
        note="Presenting concerns · property address · verdict — printed on the report"
      >
        {/* Presenting concerns — one per line → report §A bullets. */}
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Presenting concerns</label>
          <textarea
            style={{ ...field, minHeight: 96, resize: 'vertical', lineHeight: 1.5 }}
            value={reportCtx.presenting_concerns_text}
            onChange={(e) =>
              setReportCtx((c) => ({ ...c, presenting_concerns_text: e.target.value }))
            }
            placeholder={
              'The client’s stated problems / goals — one per line, e.g.\nCareer growth has stalled\nFrequent disagreements at home\nDifficulty sleeping in the master bedroom'
            }
            aria-label="Presenting concerns, one per line"
          />
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            One concern per line. These open the report (Section A · Walkthrough
            &amp; Pattern Correlation). Leave blank if none were stated.
          </p>
        </div>

        {/* Property address — printed on the cover; blank → derived from site. */}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Property address</label>
          <input
            style={field}
            value={reportCtx.property_address}
            onChange={(e) =>
              setReportCtx((c) => ({ ...c, property_address: e.target.value }))
            }
            placeholder="e.g. 12 Vijay Nagar, Indore, MP 452010"
            aria-label="Property address"
          />
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            Printed on the report cover. If left blank, the cover falls back to
            the site location (coordinates / map link) entered above.
          </p>
        </div>

        {/* ── Verdict & Recommendation (curation) — renders in L1A §D. Blank →
            §D keeps its "to be completed at curation" frame (graceful degrade). ── */}
        <div
          style={{
            marginTop: 4,
            paddingTop: 16,
            borderTop: '1px dashed var(--rule)',
          }}
        >
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 2,
            }}
          >
            Verdict &amp; Recommendation (curation)
          </div>
          <p
            style={{
              fontSize: 11.5,
              color: 'var(--ink-mute)',
              margin: '0 0 12px',
            }}
          >
            The overall call across the Vastu · Astro-Vastu · remedy-load lenses.
            Renders inside the report’s Section D. Leave blank to keep the
            “to be completed at curation” placeholder.
          </p>

          {/* Verdict statement — the headline recommendation (1–3 lines). */}
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Verdict statement</label>
            <textarea
              style={{ ...field, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }}
              value={reportCtx.verdict_text}
              onChange={(e) =>
                setReportCtx((c) => ({ ...c, verdict_text: e.target.value }))
              }
              placeholder={
                'The headline recommendation — short, e.g.\nThis property is recommended for the family, with focused remedies on two zones.'
              }
              aria-label="Verdict statement"
            />
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              1–3 lines. Shown as the lead line of Section D.
            </p>
          </div>

          {/* Reasoning — the longer cross-lens reasoning (multi-line). */}
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Reasoning</label>
            <textarea
              style={{ ...field, minHeight: 110, resize: 'vertical', lineHeight: 1.5 }}
              value={reportCtx.verdict_reasoning_text}
              onChange={(e) =>
                setReportCtx((c) => ({ ...c, verdict_reasoning_text: e.target.value }))
              }
              placeholder={
                'The reasoning across the lenses — one paragraph per line, e.g.\nThe entrances read favourably on their 32-pada positions.\nThe Astro-Vastu bedroom fit is achievable for all three members.\nThe remedy load is light — two zones only.'
              }
              aria-label="Verdict reasoning"
            />
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              One paragraph per line. Shown under “The reasoning” in Section D.
            </p>
          </div>

          {/* Recommendation status chip (optional). */}
          <div style={{ marginBottom: 4 }}>
            <label style={lbl}>Recommendation status</label>
            <select
              style={{ ...field, maxWidth: 320 }}
              value={reportCtx.verdict_status}
              onChange={(e) =>
                setReportCtx((c) => ({
                  ...c,
                  verdict_status: e.target.value as ReportContextForm['verdict_status'],
                }))
              }
              aria-label="Recommendation status"
            >
              <option value="">No status chip</option>
              <option value="recommended">Recommended</option>
              <option value="recommended_with_remedies">
                Recommended with remedies
              </option>
              <option value="not_recommended">Not recommended</option>
            </select>
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
              Optional. Shown as a coloured chip beside the verdict.
            </p>
          </div>
        </div>

        {/* ── Room allocation (curation) — override who sleeps where, overriding
            the engine's Astro-Vastu draft (L1A §E/§F). Each row maps a member
            (or the literal "couple" for the married pair) to a marked bedroom.
            Persists to answers.room_overrides; the override WINS over the engine
            default (OQ-5). No rows → the engine's couple-grouped default renders. ── */}
        <div
          style={{
            marginTop: 4,
            paddingTop: 16,
            borderTop: '1px dashed var(--rule)',
          }}
        >
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 2,
            }}
          >
            Room allocation (curation)
          </div>
          <p
            style={{
              fontSize: 11.5,
              color: 'var(--ink-mute)',
              margin: '0 0 12px',
            }}
          >
            Override who sleeps where, overriding the engine’s Astro-Vastu draft
            (report Sections E &amp; F). Type the member’s name — or the literal{' '}
            <code>couple</code> for the married pair (they share one bedroom) — and
            pick the bedroom zone. Leave empty to keep the engine’s default
            allocation (the couple is auto-grouped into one room).
          </p>

          {reportCtx.room_overrides.length === 0 ? (
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px' }}>
              No overrides — the engine allocates rooms (couple shares one bedroom).
            </p>
          ) : (
            reportCtx.room_overrides.map((row, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  marginBottom: 10,
                }}
              >
                <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                  <label style={lbl}>Member (or “couple”)</label>
                  <input
                    style={field}
                    value={row.member}
                    onChange={(e) => patchRoomOverride(i, { member: e.target.value })}
                    placeholder="e.g. couple · Tarveen · Kavyansh"
                    aria-label={`Room override member ${i + 1}`}
                  />
                </div>
                <div style={{ flex: '0 1 120px', minWidth: 110 }}>
                  <label style={lbl}>Bedroom zone</label>
                  <select
                    style={field}
                    value={row.zone}
                    onChange={(e) => patchRoomOverride(i, { zone: e.target.value })}
                    aria-label={`Room override zone ${i + 1}`}
                  >
                    <option value="">— zone —</option>
                    {VALID_BEDROOM_ZONES.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: '0 1 150px', minWidth: 120 }}>
                  <label style={lbl}>Floor (optional)</label>
                  <select
                    style={field}
                    value={row.floorId}
                    onChange={(e) => patchRoomOverride(i, { floorId: e.target.value })}
                    aria-label={`Room override floor ${i + 1}`}
                  >
                    <option value="">— any marked —</option>
                    {form.floors.map((fl) => (
                      <option key={fl.id} value={fl.id}>
                        {fl.name || fl.id}
                      </option>
                    ))}
                  </select>
                </div>
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => removeRoomOverride(i)}
                >
                  Remove
                </Btn3>
              </div>
            ))
          )}

          <div style={{ marginTop: 2 }}>
            <Btn3 intent="ghost" size="sm" onClick={addRoomOverride}>
              + Add room override
            </Btn3>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <Btn3
            intent="grn"
            size="sm"
            onClick={onSaveReportContext}
            disabled={savingReportCtx}
          >
            {savingReportCtx ? 'Saving…' : 'Save report context'}
          </Btn3>
        </div>
      </Box>}

      {/* ── Candidate properties — suppressed when floorsOnly=true ── */}
      {/* DEDICATED WORKSPACE (v3.5 / decisions #1): the entire candidate-setup
          card — candidate properties, grid-analysis rollup, per-property A/B
          checklist, +Add candidate, property label/address, per-property
          recommendation and the §D recommended-property verdict — is NOT rendered
          in the evaluation workspace. It stays on the Project page. */}
      {!floorsOnly && !workspaceMode && <Box
        title="Candidate properties"
      >
        {/* DECLUTTER 2026-07-02 (owner sign-off): the "Add each property…"
            subtitle note and the "This engagement weighs N candidate…"
            explainer paragraph are REMOVED. */}

        {/* Grid-analysis rollup REMOVED (2026-07-01) — it duplicated the
            identical strip in the merged L1A "Evaluate" header (ProjectDetail),
            which reads the same gridSummary. One rollup, one place. */}

        {/* L1A merged CANDIDATE LIST (declutter 2026-07-02, owner sign-off). ONE
            list per candidate — the old status-checklist + the separate pill
            selector are unified into a single row that: (a) shows the property
            name + its Grid ✓ · Entrance ✓ · AUO ✓/— chips + evaluated/in-progress;
            (b) SELECTS the property on click (highlight + opens its detail below,
            preserving the pill selector's switchProperty behaviour); and (c)
            carries the per-property "Open workspace →" deep-link that used to sit
            in ProjectDetail's standalone links row. The active property's live
            floors come from the working buffer (form.floors); the others from the
            committed props_. Row is a DIV (not a button) so the select-area button
            and the workspace Link never nest interactive elements. */}
        {evalChecklist && props_.some((p) => isCandidateLinkVisible(p, linkedPropertyIds)) && (
          <div
            data-testid="l1a-per-property-checklist"
            style={{
              marginBottom: 10,
              border: '1px solid var(--rule)',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            {props_.map((p, i) => {
              // LINK-VISIBLE drop (owner ruling 2026-07-03: "a correction must
              // propagate") — a candidate whose stamped propertyId was UNLINKED
              // at Intake (fell out of a NON-EMPTY live projects.propertyRefs[])
              // is dropped from this list the moment the unlink lands. An EMPTY
              // refs list is NO link data and never hides (VP678 rule — see
              // isCandidateLinkVisible). Read-side only: the intake slot
              // (floors/gradings) survives, so re-linking the property restores
              // the row. Candidates with no FK (raw client-submitted slots)
              // always stay.
              if (!isCandidateLinkVisible(p, linkedPropertyIds)) return null;
              const pFloors = i === activeProp ? form.floors : p.floors;
              const grid = pFloors.some(floorHasBoundary);
              const entrance = pFloors.some(floorHasEntrance);
              const evaluated = grid && entrance;
              const auoDone = evaluated && pFloors.every(floorAuoResolved);
              const sel = i === activeProp;
              const label = p.label?.trim() || `Property ${String.fromCharCode(65 + i)}`;
              const canOpenWorkspace = !!(clientId && projectId && p.propertyId);
              const Mark = ({ ok, dash, name }: { ok: boolean; dash?: boolean; name: string }) => (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    color: ok ? 'var(--good, #2e7d32)' : dash ? 'var(--ink-mute)' : 'var(--warn, #9a6a12)',
                    fontWeight: 600,
                  }}
                >
                  <span aria-hidden>{ok ? '✓' : dash ? '—' : '○'}</span>
                  {name}
                </span>
              );
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    // First VISIBLE row (not index 0 — earlier rows may be
                    // link-dropped) carries no top rule.
                    borderTop:
                      i === props_.findIndex((c) => isCandidateLinkVisible(c, linkedPropertyIds))
                        ? 'none'
                        : '1px solid var(--rule)',
                    borderLeft: sel ? '3px solid var(--accent, #2f5d8a)' : '3px solid transparent',
                    background: sel ? 'var(--accent-soft, #eef4fb)' : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => switchProperty(i)}
                    aria-pressed={sel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      padding: '9px 12px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: sel ? 700 : 600,
                        color: 'var(--ink)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </span>
                    <Mark ok={grid} name="Grid" />
                    <Mark ok={entrance} name="Entrance" />
                    {/* AUO is optional to proceed (AD-048) — show ✓ when resolved, — when
                        none/not-yet (a dash, not a warning). */}
                    <Mark ok={auoDone} dash name="AUO" />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: evaluated ? 'var(--good, #2e7d32)' : 'var(--ink-mute)',
                        minWidth: 78,
                        textAlign: 'right',
                      }}
                    >
                      {evaluated ? 'evaluated' : 'in progress'}
                    </span>
                  </button>
                  {canOpenWorkspace && (
                    <Link
                      to={`/evaluate/${clientId}/${projectId}/${p.propertyId}`}
                      data-testid={`open-eval-workspace-${p.propertyId}`}
                      title={`Open the full-screen evaluation workspace for ${label}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        flexShrink: 0,
                        marginRight: 10,
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--accent)',
                        textDecoration: 'none',
                        border: '1px solid var(--rule-strong)',
                        borderRadius: 'var(--r-sm)',
                        padding: '5px 9px',
                        minHeight: 30,
                        background: 'var(--surface, #fff)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Open workspace →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* FIX A (owner bug 2026-07-03) — the EXPLICIT zero-candidate state.
            Removing the last candidate is allowed now, so the box must say so
            instead of rendering nothing. The add path for L1A is the
            authoritative link path (see the NOTE below) — a real navigation
            link to the Intake stage, never a dead control. */}
        {evalChecklist && !loading && props_.length === 0 && (
          <EmptyNote title="No candidate properties">
            Every candidate has been removed from this engagement. To add one,
            link a registry property at{' '}
            {projectId ? (
              <Link
                to={`/project/${projectId}?tab=intake`}
                style={{ color: 'var(--accent)', fontWeight: 600 }}
              >
                Intake → Linked Properties
              </Link>
            ) : (
              <b>Intake → Linked Properties</b>
            )}{' '}
            (“+ Link property”) — it appears here as a gradeable candidate.
          </EmptyNote>
        )}

        {/* NOTE (2026-07-02): the L1A "+ Add candidate property" blank-slot adder
            was REMOVED here. Candidates now enter `answers.properties[]` ONLY by
            linking a real registry property at intake / project-setup ("+ Link
            property" in the Linked-Properties panel → linkPropertyAsCandidate →
            planCandidateForLink appends a pre-stamped candidate slot). The blank
            adder duplicated/confused that authoritative path. `addProperty()` is
            retained — it still backs the non-L1A pill-selector add below. */}

        {/* property pills — legacy selector for non-L1A mounts (evalChecklist off).
            In the L1A Evaluate tab the merged candidate list above replaces these,
            so they are suppressed to avoid two stacked representations. */}
        {!evalChecklist && <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}
        >
          {props_.map((p, i) => {
            const sel = i === activeProp;
            // the ACTIVE property's live floors are in the working buffer (form.floors);
            // the others are committed in props_. Grade against whichever is current.
            const pFloors = i === activeProp ? form.floors : p.floors;
            const floorN = pFloors.length;
            const gStatus: GridStatus = candidateGridStatus({ id: p.id, floors: pFloors });
            const gBadge = gridBadge(gStatus);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => switchProperty(i)}
                aria-pressed={sel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 12px',
                  borderRadius: 8,
                  border: sel
                    ? '1.5px solid var(--accent, #2f5d8a)'
                    : '1px solid var(--rule)',
                  background: sel ? 'var(--accent-soft, #eef4fb)' : 'transparent',
                  color: 'var(--ink)',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: sel ? 700 : 500,
                  cursor: 'pointer',
                }}
              >
                <span>{p.label?.trim() || `Property ${String.fromCharCode(65 + i)}`}</span>
                <Tag kind="default">
                  {floorN} floor{floorN === 1 ? '' : 's'}
                </Tag>
                <Tag
                  kind={gBadge.intent === 'good' ? 'val' : gBadge.intent === 'warn' ? 'warn' : 'default'}
                  title={
                    gStatus === 'complete'
                      ? 'Every floor: closed grid + entrance + AUOs confirmed — fully gridded'
                      : gStatus === 'partial'
                        ? 'Grid ready to use (boundary + entrance) — AUOs can still be added; you can already generate'
                        : 'Trace a boundary + mark an entrance to make this property report-ready'
                  }
                >
                  {gBadge.label}
                </Tag>
                {recommendedPropId &&
                  (recommendedPropId === p.propertyId || recommendedPropId === p.id) && (
                    <Tag kind="val">recommended</Tag>
                  )}
              </button>
            );
          })}
          <Btn3 intent="ink" size="sm" onClick={addProperty}>
            + Add candidate property
          </Btn3>
        </div>}

        {/* active property meta */}
        {props_[activeProp] && (
          <Card flat style={{ padding: 12 }}>
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '2 1 200px' }}>
                <label style={lbl}>Property label</label>
                <input
                  style={field}
                  value={props_[activeProp].label}
                  onChange={(e) =>
                    patchPropertyMeta(activeProp, { label: e.target.value })
                  }
                  placeholder={`e.g. Property ${String.fromCharCode(65 + activeProp)} — 12 Oak St`}
                />
              </div>
              <div style={{ flex: '3 1 240px' }}>
                <label style={lbl}>Property address</label>
                <input
                  style={field}
                  value={props_[activeProp].property_address}
                  onChange={(e) =>
                    patchPropertyMeta(activeProp, {
                      property_address: e.target.value,
                    })
                  }
                  placeholder="Address printed in §B of the report"
                />
              </div>
              {/* FIX A (owner bug 2026-07-03): on the L1A Evaluate mount
                  (evalChecklist) the remove works for EVERY candidate —
                  including the last one. Non-L1A mounts keep the ORIGINAL
                  multi-slot-only scope (Vera gate MEDIUM-2 / Larry ruling:
                  the owner asked for L1A only — a single-slot L2B property
                  must not gain a wipe button). Destructive → inline two-step
                  confirm (arm, then confirm within 4s), never window.confirm. */}
              {(evalChecklist || props_.length > 1) && (
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={onRemoveClick}
                  disabled={removeBusy}
                  aria-label={
                    removeArmed
                      ? 'Confirm removing this candidate property'
                      : 'Remove this candidate property'
                  }
                  title="Removes this candidate from the engagement — its intake slot and its registry link on this project are deleted (the registry property record itself is kept)"
                  style={
                    removeArmed
                      ? {
                          color: 'var(--crit)',
                          borderColor: 'var(--crit)',
                          fontWeight: 700,
                        }
                      : undefined
                  }
                >
                  {removeBusy
                    ? 'Removing…'
                    : removeArmed
                      ? 'Confirm remove?'
                      : 'Remove property'}
                </Btn3>
              )}
            </div>

            {/* "Push to registry" removed (2026-06-28, Gap #6). Property IS the
                registry; report immutability is via versioned PDFs (L1A_v{n}) — no
                manual push needed. We keep a read-only registry-link indicator. */}
            {props_[activeProp]?.propertyId && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Tag kind="val" title="This candidate is linked to a registry property">
                  registry {props_[activeProp]?.propertyId}
                </Tag>
                <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                  Linked to the property registry. The boundary &amp; grid traced on
                  the Property page are the single source of truth the report reads.
                </span>
              </div>
            )}

            {/* 2.2 — PER-PROPERTY recommendation (editable + AI-draftable). The
                Acharya's call FOR THIS candidate, set right in the evaluation step.
                The project-level §D verdict TEXT is a single "Summarize" over all
                of these (Verdict step). Saved with the properties (saveProperties). */}
            {props_[activeProp] && (
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: '1px dashed var(--rule)',
                }}
                data-testid="per-property-recommendation"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  <label style={{ ...lbl, marginBottom: 0 }}>
                    Recommendation for this property
                  </label>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    disabled={recDraftBusy}
                    onClick={() => {
                      if (recDraftBusy) return; // guard double-fire
                      const idx = activeProp;
                      const fl = form.floors;
                      // Local heuristic draft (always available, no backend).
                      const localDraft = () =>
                        draftPropertyRecommendation({
                          label:
                            props_[idx].label?.trim() ||
                            `Property ${String.fromCharCode(65 + idx)}`,
                          hasGrid: fl.some(floorHasBoundary),
                          hasEntrance: fl.some(floorHasEntrance),
                          auoResolved: fl.length > 0 && fl.every(floorAuoResolved),
                          floorCount: fl.length,
                        });
                      // PREFER Forge's AI-assisted callable (Evaluate mockup §B);
                      // it may be undeployed (Forge builds it in parallel), in
                      // which case generatePropertyRecommendation() resolves null
                      // and we fall back to the local heuristic. Never throws.
                      const pid = props_[idx]?.propertyId || '';
                      setRecDraftBusy(true);
                      void (async () => {
                        try {
                          const ai = pid && projectId
                            ? await generatePropertyRecommendation({
                                projectId,
                                propertyId: pid,
                              })
                            : null;
                          if (ai) {
                            // Compose the editable reasoning from the plain-language
                            // synthesis + the transparency basis bullets, so nothing
                            // the engine drew on is lost when the evaluator edits.
                            const bullets = [
                              ...ai.basis.vastu.map((b) => `• ${b}`),
                              ...(ai.basis.astro ? [`• Astro fit — ${ai.basis.astro}`] : []),
                            ];
                            const reasoning = [ai.reasoning, ...bullets]
                              .filter((s) => s && s.trim().length > 0)
                              .join('\n');
                            patchPropertyMeta(idx, {
                              recommendation_status: toVerdictStatus(ai.recommendation),
                              recommendation_reasoning: reasoning,
                            });
                            onMsg(
                              `AI-assisted draft (confidence: ${ai.confidence}) — edit, then Save.`,
                            );
                          } else {
                            // FALLBACK — the callable is undeployed/unavailable, so
                            // this draft came from the LOCAL heuristic, not the AI.
                            // Toast must not claim "AI" (honesty, Vera QA).
                            const draft = localDraft();
                            patchPropertyMeta(idx, {
                              recommendation_status: draft.status,
                              recommendation_reasoning: draft.reasoning,
                            });
                            onMsg(
                              "Drafted from this property's evaluation — edit, then Save.",
                            );
                          }
                        } finally {
                          setRecDraftBusy(false);
                        }
                      })();
                    }}
                    title="Draft a recommendation from this property's evaluation (grid · entrance · AUO) — AI-assisted when available, editable. Confirm with Save."
                  >
                    <span aria-hidden>✦</span> {recDraftBusy ? 'Drafting…' : 'AI draft'}
                  </Btn3>
                </div>
                <select
                  style={{ ...field, maxWidth: 320 }}
                  value={normaliseVerdictStatus(props_[activeProp].recommendation_status)}
                  onChange={(e) =>
                    patchPropertyMeta(activeProp, {
                      recommendation_status: e.target.value as VerdictStatus,
                    })
                  }
                  aria-label="Per-property recommendation status"
                >
                  <option value="">— not assessed —</option>
                  <option value="recommended">Recommended</option>
                  <option value="recommended_with_remedies">
                    Recommended with remedies
                  </option>
                  <option value="not_recommended">Not recommended</option>
                </select>
                <textarea
                  style={{
                    ...field,
                    marginTop: 8,
                    minHeight: 60,
                    resize: 'vertical',
                    lineHeight: 1.5,
                  }}
                  value={props_[activeProp].recommendation_reasoning ?? ''}
                  onChange={(e) =>
                    patchPropertyMeta(activeProp, {
                      recommendation_reasoning: e.target.value,
                    })
                  }
                  placeholder="Why this call for this property — the per-property reasoning the §D summary draws on."
                  aria-label="Per-property recommendation reasoning"
                />
              </div>
            )}

            {/* MOVED 2026-07-02 (owner sign-off): the "Recommended property (§D
                cross-property verdict)" selector is relocated OUT of the Evaluate
                screen and INTO the Verdict stage (ReportContextCard → VerdictField),
                where the §D verdict itself is authored. It reads/writes the SAME
                underlying field (answers.recommended_property_id). This panel still
                LOADS `recommendedPropId` on mount and passes it through on every
                saveProperties call (floor saves), so the stored pick is preserved,
                never clobbered — only its editing UI moved. */}
          </Card>
        )}
      </Box>}

      {/* ── Floors: floor-plan upload + boundary tracing + grid preview ── */}
      {/* When Property entities are linked, floors are managed on the Property  */}
      {/* page — suppress this section to avoid duplicate upload paths.          */}
      {/* LAYER-1 FIX (clean-flow spec §3.3): for the L1A project mount           */}
      {/* (hideGridTool && isL1A) we DON'T collapse to the static box — the staff */}
      {/* MARK entrances/AUO on the linked property's locked grid right here. The */}
      {/* static "Managed on linked Properties" box stays for every other tier    */}
      {/* (L2B/L2C…) so they remain byte-identical.                               */}
      {/* FIX A (owner bug 2026-07-03): with ZERO candidates on the L1A Evaluate  */}
      {/* mount there is nothing to grid/mark — the whole floors section is       */}
      {/* suppressed (the Candidate-properties empty note above carries the       */}
      {/* add-back path), so no dead "+ Add floor"/"Save" controls render.        */}
      {evalChecklist && props_.length === 0 ? null : linkedPropertyIds.length > 0 && !(hideGridTool && isL1A) ? (
        <Box title="Floors & grid (staff)" note="Managed on linked Properties">
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
            Floor plans are managed on the linked{' '}
            {linkedPropertyIds.length === 1 ? 'Property' : 'Properties'} (
            {linkedPropertyIds.join(', ')}). Open each property to upload plans and trace
            the grid.
          </p>
        </Box>
      ) : (
      <FloorsWrap
        title={
          multiProperty
            ? `Floors & grid — ${props_[activeProp]?.label?.trim() || `Property ${String.fromCharCode(65 + activeProp)}`}`
            : 'Floors & grid (staff)'
        }
        note="Upload each floor plan, trace the outline — the grid generates on the plan"
      >
        {!workspaceMode && (
        <p
          style={{
            fontSize: 11.5,
            color: 'var(--ink-mute)',
            margin: '0 0 12px',
          }}
        >
          The grid is the foundation of the whole analysis. Open a floor below,
          trace the outer boundary (the grid generates on the plan), then switch
          to “Mark entrances”, press “+ Add door” and click a wall — its pada is
          read off the grid, never typed blind. Drag the door’s end grips to set
          its exact span. Pick which door is the main entry, then Save. The grid
          honours the true-north bearing above.
        </p>
        )}

        {form.floors.length === 0 && (
          <EmptyNote title="No floors yet">
            Add each floor of the unit, upload its plan, then trace the outer
            boundary.
          </EmptyNote>
        )}

        {/* ── ACCORDION (item 6): a LIST of floor rows; click a name to expand
            its full work panel; one floor open at a time. ── */}
        {form.floors.map((fl, fi) => {
          // WORKSPACE (v3.5): one property = one primary surface (floor 0). Render
          // only floor 0, forced open, with NO collapse header — just the canvas.
          if (workspaceMode && fi !== 0) return null;
          const open = workspaceMode ? true : openFloor === fi;
          const sum = floorSummary(fl);
          return (
            <Card key={fl.id} flat style={{ marginBottom: 10, padding: 0 }}>
              {/* clickable header row (collapsed summary lives here) */}
              {!workspaceMode && (
              <button
                type="button"
                onClick={() => toggleFloor(fi)}
                aria-expanded={open}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  padding: '11px 12px',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-mute)',
                    transform: open ? 'rotate(90deg)' : 'none',
                    transition: 'transform 120ms',
                    display: 'inline-block',
                  }}
                >
                  ▶
                </span>
                <span
                  style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}
                >
                  {fl.name?.trim() || `Floor ${fi + 1}`}
                </span>
                {/* compact summary chips (collapsed view) */}
                <span
                  style={{
                    display: 'inline-flex',
                    gap: 6,
                    marginLeft: 'auto',
                    flexWrap: 'wrap',
                  }}
                >
                  <Tag kind={sum.hasPlan ? 'val' : 'default'}>
                    {sum.hasPlan ? 'plan ✓' : 'no plan'}
                  </Tag>
                  <Tag kind={sum.gridReady ? 'val' : 'default'}>
                    {sum.gridReady ? 'grid ✓' : 'no grid'}
                  </Tag>
                  <Tag kind="default">
                    {sum.doorCount} door{sum.doorCount === 1 ? '' : 's'}
                  </Tag>
                </span>
              </button>
              )}

              {/* expanded work panel */}
              {open && (
                <div
                  style={{
                    padding: '0 12px 12px',
                    borderTop: '1px solid var(--rule)',
                  }}
                >
                  {/* WORKSPACE (v3.5 §E): the floor name input + "Remove floor"
                      clutter the map pane — floor switching is the header dropdown
                      only. Suppressed in the dedicated workspace. */}
                  {!workspaceMode && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-end',
                      flexWrap: 'wrap',
                      margin: '12px 0 10px',
                    }}
                  >
                    <div style={{ flex: '2 1 180px' }}>
                      <label style={lbl}>Floor / surface name</label>
                      {/* Creatable combobox (UNIFIED with the Properties floor
                          surface) — type a name, pick a preset, or add a free
                          value like "Site / plot map" (owner directive 2026-06-26). */}
                      <FloorNameCombobox
                        value={fl.name}
                        onChange={(v) => patchFloor(fi, { name: v })}
                        placeholder="e.g. Site / plot map, Ground Floor…"
                        ariaLabel="Floor or surface name"
                      />
                    </div>
                    <Btn3
                      intent="ghost"
                      size="sm"
                      onClick={() => removeFloor(fi)}
                      aria-label="Remove floor"
                    >
                      Remove floor
                    </Btn3>
                  </div>
                  )}

                  {/* ── AUO WORKSPACE — three FIXED regions (Slice 5 redesign
                      2026-06-08, owner ask). The map + its toolbar stay PUT while
                      the side review list (every AUO stacked + numbered) scrolls
                      independently — no more scroll-up-for-map, scroll-down-for-
                      buttons. Desktop = map sticky on the LEFT, review list on the
                      RIGHT. Mobile/Capacitor = map full-width (the primary
                      surface), the review list becomes a thumb-reachable BOTTOM
                      SHEET (toggle), with safe-area insets. ONE responsive layout,
                      not a fork. ── */}
                  {(() => {
                    const auoPanel = (
                      <AuoReviewPanel
                        marks={auoMarksFor(fl)}
                        palette={palette}
                        busy={auoBusyFloor === fi}
                        canSuggest={isValidBoundary(fl.boundary)}
                        selectedId={selectedAuoId}
                        onSelect={setSelectedAuoId}
                        onSuggest={() => onSuggestAuo(fi)}
                        onSetName={(id, n) => onSetAuoName(fi, id, n)}
                        onSetType={(id, t) => onSetAuoType(fi, id, t)}
                        onSetNameType={(id, n, t) => onSetAuoNameType(fi, id, n, t)}
                        onSetZones={(id, z) => onSetAuoZones(fi, id, z)}
                        onSetShape={(id, k) => onSetAuoShape(fi, id, k)}
                        onRotate={(id, deg) => onRotateAuo(fi, id, deg)}
                        onConfirm={(id) => onConfirmAuo(fi, id)}
                        onReject={(id) => onRejectAuo(fi, id)}
                        onReopen={(id) => onReopenAuo(fi, id)}
                        onRemove={(id) => onRemoveAuo(fi, id)}
                        isAdmin={isAdmin}
                        onCorrectDisposition={(id, zone, patch) =>
                          onCorrectAuoDisposition(fi, id, zone, patch)
                        }
                        onInlineAdd={(name) => {
                          // a custom AUO not in the palette → flow it to the
                          // pending collection for admin/Pitra promotion (best-effort).
                          if (palette) {
                            void recordPendingAuo(
                              paletteFileFor(palette.file),
                              name,
                              { uid: actor.uid, name: actor.name },
                            );
                          }
                        }}
                      />
                    );
                    // WORKSPACE UNIFIED LIST (owner-confirmed 2026-07-01): the
                    // "AUO disposition" tab is ONE list — doors + AUOs + markups in
                    // one running sequence NUMBERED TO MATCH THE MAP PINS (evalDispoItems
                    // seqNo + the markup offset). Rows expand to their FEATURE CARD; the
                    // AUO card is the REAL shared `AuoCardBody` (byte-identical, not a
                    // redraw). Portalled into the right pane exactly like auoPanel was,
                    // so it holds the REAL marks + ops incl. the authoritative confirm.
                    // fi === 0 here (workspace renders only floor 0).
                    const unifiedList = (
                      <UnifiedDispoList
                        items={evalDispoItems}
                        auoMarks={auoMarksFor(fl)}
                        markups={evalMarkupItems}
                        palette={palette}
                        isAdmin={isAdmin}
                        canSuggest={isValidBoundary(fl.boundary)}
                        selectedId={selectedMarkupId ?? selectedAuoId}
                        onSelect={(id) => {
                          // markup ids (mk…) vs door/AUO ids (en…/au…) are distinct
                          // namespaces — route the highlight to the right channel so
                          // the map echoes the correct marker.
                          if (id && id.startsWith('mk')) {
                            setSelectedMarkupId(id);
                            setSelectedAuoId(null);
                          } else {
                            setSelectedAuoId(id);
                            setSelectedMarkupId(null);
                          }
                        }}
                        onSuggest={() => onSuggestAuo(fi)}
                        onSetName={(id, n) => onSetAuoName(fi, id, n)}
                        onSetType={(id, t) => onSetAuoType(fi, id, t)}
                        onSetNameType={(id, n, t) => onSetAuoNameType(fi, id, n, t)}
                        onSetZones={(id, z) => onSetAuoZones(fi, id, z)}
                        onSetShape={(id, k) => onSetAuoShape(fi, id, k)}
                        onRotate={(id, deg) => onRotateAuo(fi, id, deg)}
                        onConfirm={(id) => onConfirmAuo(fi, id)}
                        onReject={(id) => onRejectAuo(fi, id)}
                        onReopen={(id) => onReopenAuo(fi, id)}
                        onRemove={(id) => onRemoveAuo(fi, id)}
                        onCorrectDisposition={(id, zone, patch) =>
                          onCorrectAuoDisposition(fi, id, zone, patch)
                        }
                        onInlineAdd={(name) => {
                          if (palette) {
                            void recordPendingAuo(
                              paletteFileFor(palette.file),
                              name,
                              { uid: actor.uid, name: actor.name },
                            );
                          }
                        }}
                        onSetDoorType={(id, t) => onSetEntranceType(fi, id, t)}
                        onSetPrincipal={(id) => onSetPrincipal(fi, id)}
                        onRemoveDoor={(id) => onRemoveEntrance(fi, id)}
                        onSetMarkupNote={(id, note) => onSetMarkupNote(id, note)}
                        onSetMarkupLabel={(id, label) => onSetMarkupLabel(id, label)}
                        onRemoveMarkup={(id) => onRemoveMarkup(id)}
                      />
                    );
                    // ONE GRID PLACE (owner directive 2026-06-26): when
                    // `hideGridTool` is set (the project mount), the in-project
                    // boundary tracer / AUO marking is replaced by a pointer to
                    // the Property page — the project is NOT a second tracer. The
                    // floor NAME (combobox) stays. The tracer code path is retained
                    // (not deleted) for a clean later cut. ("Push to registry" was
                    // removed 2026-06-28, Gap #6 — property IS the registry.)
                    // AD-013 / L1A — STATE-AWARE pointer (shown when there is no
                    // marking surface to render): the candidate isn't linked to a
                    // property yet, OR its property has no traced grid yet, OR this
                    // is a non-primary floor (L1A marks the primary surface here;
                    // extra floors are gridded on the Property page), OR the grid is
                    // still loading. Each case is actionable, never a dead surface.
                    const linkedPid = props_[activeProp]?.propertyId || '';
                    const gridPointer = (
                      <div
                        style={{
                          border: '1px dashed var(--rule)',
                          borderRadius: 'var(--r-sm)',
                          background: 'var(--inset, #f6f7f9)',
                          padding: '12px 14px',
                          fontSize: 12.5,
                          color: 'var(--ink-soft)',
                          lineHeight: 1.55,
                        }}
                      >
                        {propGridLoading && fi === 0 ? (
                          <>Loading the property grid…</>
                        ) : !linkedPid ? (
                          <>
                            <strong style={{ color: 'var(--ink)' }}>
                              Link &amp; grid this property first.
                            </strong>{' '}
                            This candidate isn’t linked to a registry property yet. Link or
                            create one in <b>Linked Properties</b> above, then open{' '}
                            <b>Properties → that property → “Open grid”</b> to trace its
                            boundary. Entrance &amp; AUO marking unlocks here once the grid
                            exists.
                          </>
                        ) : fi === 0 ? (
                          <>
                            <strong style={{ color: 'var(--ink)' }}>
                              No grid on this property yet.
                            </strong>{' '}
                            Open <b>Properties → {linkedPid} → “Open grid”</b> and trace the
                            boundary. Come back here and you’ll mark entrances &amp; AUO
                            directly on that locked grid.
                            <div style={{ marginTop: 4, color: 'var(--ink-mute)' }}>
                              Linked registry property: <b>{linkedPid}</b>
                            </div>
                          </>
                        ) : (
                          <>
                            <strong style={{ color: 'var(--ink)' }}>
                              Primary surface marked above.
                            </strong>{' '}
                            For an L1A selection review you mark the primary surface (the
                            first floor / site map). Additional floors for{' '}
                            <b>{linkedPid}</b> are gridded on the Property page.
                          </>
                        )}
                      </div>
                    );
                    // PLAN-ONLY (Gap #1) — the linked property has an uploaded map
                    // but NO traced boundary yet. SHOW the map (it was seeded into
                    // this floor) + a "Trace boundary → Property page" link, instead
                    // of the bare pointer. VISIBILITY only: no marking surface (there
                    // is no grid), so the candidate stays not grid-ready / unevaluated.
                    const planOnlyView = (
                      <div
                        style={{
                          border: '1px dashed var(--rule)',
                          borderRadius: 'var(--r-sm)',
                          background: 'var(--inset, #f6f7f9)',
                          padding: 12,
                        }}
                      >
                        {propGrid?.floorPlan?.downloadURL && (
                          <img
                            src={propGrid.floorPlan.downloadURL}
                            alt={`Uploaded map for ${linkedPid} — boundary not traced yet`}
                            style={{
                              display: 'block',
                              width: '100%',
                              maxHeight: 360,
                              objectFit: 'contain',
                              background: '#fff',
                              borderRadius: 'var(--r-sm)',
                              border: '1px solid var(--rule)',
                            }}
                          />
                        )}
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 12.5,
                            color: 'var(--ink-soft)',
                            lineHeight: 1.55,
                          }}
                        >
                          <strong style={{ color: 'var(--ink)' }}>
                            Map uploaded — boundary not traced yet.
                          </strong>{' '}
                          This map is visible but not yet grid-ready.{' '}
                          <a
                            href={`/properties/${linkedPid}`}
                            style={{ color: 'var(--accent)', fontWeight: 600 }}
                          >
                            Trace boundary — Open grid on the Property page →
                          </a>{' '}
                          Entrance &amp; AUO marking unlocks here once the grid exists.
                        </div>
                      </div>
                    );
                    const tracer = (
                      <FloorBoundaryTracer
                        floorplan={fl.floorplan}
                        boundary={fl.boundary}
                        north={northForGrid}
                        uploading={uploadingFloor === fi}
                        projectImageFiles={projectImageFiles}
                        onUpload={(file) => onUploadPlan(fi, file)}
                        onPickProjectFile={(src) => onPickProjectFile(fi, src)}
                        onClearPlan={() => onClearPlan(fi)}
                        onEditPlan={() => openPlanEditor(fi)}
                        onChangeBoundary={(pts, commit) =>
                          onChangeBoundary(fi, pts, commit)
                        }
                        entranceMarks={marksFor(fl)}
                        onAddEntrance={(pt) => onAddEntrance(fi, pt)}
                        onAddInternalEntrance={(pt) => onAddInternalEntrance(fi, pt)}
                        onMoveEntrance={(id, pt) => onMoveEntrance(fi, id, pt)}
                        onSetEntranceEnd={(id, which, pt) =>
                          onSetEntranceEnd(fi, id, which, pt)
                        }
                        onRemoveEntrance={(id) => onRemoveEntrance(fi, id)}
                        onSetEntranceType={(id, t) => onSetEntranceType(fi, id, t)}
                        onSetEntranceName={(id, n) => onSetEntranceName(fi, id, n)}
                        onSetPrincipal={(id) => onSetPrincipal(fi, id)}
                        auoMarks={auoMarksFor(fl)}
                        onAddAuo={(pt) => onAddAuo(fi, pt)}
                        onMoveAuo={(id, pt) => onMoveAuo(fi, id, pt)}
                        onResizeAuo={(id, size) => onResizeAuo(fi, id, size)}
                        onResizeAuoAnchored={(id, corner, pt) =>
                          onResizeAuoAnchored(fi, id, corner, pt)
                        }
                        onRemoveAuo={(id) => onRemoveAuo(fi, id)}
                        selectedAuoId={selectedAuoId}
                        onSelectAuo={(id) => { setSelectedAuoId(id); setSelectedMarkupId(null); }}
                        // MARKUPS (v3.5 / #2) — eval-canvas annotations. Only wired on
                        // the primary surface in the workspace (floor 0).
                        markups={workspaceMode && fi === 0 ? markupsFor(form.floors[0]) : undefined}
                        onAddMarkup={onAddMarkup}
                        onMoveMarkup={onMoveMarkup}
                        onResizeMarkup={onResizeMarkup}
                        onRemoveMarkup={onRemoveMarkup}
                        selectedMarkupId={selectedMarkupId}
                        onSelectMarkup={(id) => { setSelectedMarkupId(id); setSelectedAuoId(null); }}
                        // AD-013 / L1A — in the project mount (hideGridTool) with a
                        // resolved property grid, the boundary is LOCKED here: it was
                        // traced on the Property page + seeded into this floor; staff
                        // only MARK on it. The trace/upload/edit affordances vanish.
                        // WORKSPACE (v3.5 §D / decision #15): boundary + the 32-pada
                        // grid are EDITABLE here — not locked to the Property page —
                        // so the read-only lock is OFF in the dedicated workspace.
                        readOnlyBoundary={!workspaceMode && hideGridTool && !!propGrid && fi === 0}
                        // Phase-1 evaluation workspace (spec §3.3) — in-frame
                        // zoom/pan on the PRIMARY marking surface only. Off
                        // everywhere else (default), so existing mounts are
                        // byte-unchanged.
                        enableZoom={enableZoom && fi === 0}
                      />
                    );
                    // AD-013 / L1A — MARKING-ON-PROPERTY-GRID. In the project mount,
                    // the staff don't trace: they mark entrances + AUO on the linked
                    // property's locked grid. The PRIMARY surface (floor 0) with a
                    // resolved property grid FALLS THROUGH to the normal marking
                    // workspace below (the tracer is readOnlyBoundary; boundary +
                    // plan were seeded from the property grid; marking + the AUO
                    // confirm-loop are fully enabled). Every other case (no link, no
                    // grid yet, loading, or a non-primary floor) shows the actionable
                    // state-aware pointer instead of a dead surface.
                    // Gap #1 — a PLAN-ONLY property (map uploaded, no boundary) on the
                    // primary floor shows the map + a trace link (planOnlyView), NOT a
                    // marking surface (there is no grid to mark on). Only a REAL grid
                    // (boundary present, needsBoundary === false) falls through.
                    const hasMarkingGrid =
                      fi === 0 && !!propGrid && propGrid.needsBoundary === false;
                    const isPlanOnlyPrimary =
                      fi === 0 && !!propGrid && propGrid.needsBoundary === true;
                    if (hideGridTool && !hasMarkingGrid) {
                      return (
                        <div data-testid="grid-on-property-pointer">
                          {isPlanOnlyPrimary ? planOnlyView : gridPointer}
                        </div>
                      );
                    }

                    if (isMobile) {
                      // MOBILE / CAPACITOR — map full-width (primary), review list
                      // as a fixed BOTTOM SHEET toggled by a thumb bar. Safe-area
                      // insets so the sheet clears the home indicator / notch.
                      const auoCount = auoMarksFor(fl).length;
                      return (
                        <div data-testid="auo-workspace-mobile">
                          {tracer}
                          {/* the bottom-sheet toggle bar (thumb-reachable) */}
                          <button
                            type="button"
                            data-testid="auo-sheet-toggle"
                            onClick={() => setSheetOpen((v) => !v)}
                            style={{
                              position: 'sticky',
                              bottom: 0,
                              width: '100%',
                              marginTop: 10,
                              minHeight: 44,
                              border: '1px solid var(--rule-strong)',
                              borderRadius: 'var(--r-sm)',
                              background: 'var(--ink)',
                              color: '#fff',
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: 'inherit',
                              cursor: 'pointer',
                            }}
                          >
                            {sheetOpen ? '▼ Hide AUO review' : `▲ AUO review (${auoCount})`}
                          </button>
                          {sheetOpen && (
                            <div
                              data-testid="auo-bottom-sheet"
                              style={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 40,
                                maxHeight: '70vh',
                                background: '#fff',
                                borderTop: '1px solid var(--rule-strong)',
                                boxShadow: '0 -8px 24px rgba(30,26,21,0.18)',
                                borderTopLeftRadius: 14,
                                borderTopRightRadius: 14,
                                // safe-area: clear the home indicator + add side gutters.
                                padding: '10px 12px',
                                paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                                paddingLeft: 'calc(12px + env(safe-area-inset-left, 0px))',
                                paddingRight: 'calc(12px + env(safe-area-inset-right, 0px))',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => setSheetOpen(false)}
                                aria-label="Close AUO review"
                                style={{
                                  alignSelf: 'flex-end',
                                  border: 'none',
                                  background: 'transparent',
                                  fontSize: 18,
                                  lineHeight: 1,
                                  color: 'var(--ink-mute)',
                                  cursor: 'pointer',
                                  minHeight: 44,
                                  minWidth: 44,
                                }}
                              >
                                ✕
                              </button>
                              <div style={{ minHeight: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                                {auoPanel}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    // DEDICATED WORKSPACE (v3.5): the LEFT pane is the MAP ONLY.
                    // The AUO disposition + review list live in the RIGHT pane's
                    // "AUO disposition" tab (via the bridge), so here we render just
                    // the tracer full-width — no in-pane review column (that was the
                    // duplicate list + the horizontal overflow), keeping ONE screen
                    // with no nested scroll.
                    if (workspaceMode) {
                      return (
                        <div data-testid="auo-workspace-eval">
                          {tracer}
                          {/* the ONE unified list (doors + AUOs + markups, numbered
                              to match the map pins) portalled into the right pane's
                              "AUO disposition" tab. Its AUO rows render the REAL
                              shared AuoCardBody (same controls, same authoritative
                              confirm path — fixtures[].status → report). Rendered
                              only when that tab is mounted (host non-null). */}
                          {auoPortalHost && createPortal(unifiedList, auoPortalHost)}
                        </div>
                      );
                    }

                    // DESKTOP — 2-column workspace: the map + toolbar STICKY on the
                    // left (stays put while the right scrolls), the numbered review
                    // list on the right with its own scroll. The third "region" —
                    // the toolbar — is fixed inside the tracer at the top of the map
                    // column, always reachable.
                    return (
                      <div
                        data-testid="auo-workspace-desktop"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(300px, 1fr)',
                          gap: 16,
                          alignItems: 'start',
                        }}
                      >
                        <div style={{ position: 'sticky', top: 8, alignSelf: 'start' }}>
                          {tracer}
                        </div>
                        <div
                          style={{
                            position: 'sticky',
                            top: 8,
                            alignSelf: 'start',
                            maxHeight: 'calc(100vh - 24px)',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            border: '1px solid var(--rule)',
                            borderRadius: 'var(--r-sm)',
                            background: '#fff',
                            padding: '10px 12px',
                          }}
                        >
                          {auoPanel}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── SAVE + status (item 5): right here in the floor panel,
                      near the canvas. Button-save (not auto-save); shows an
                      unsaved-changes hint when dirty + the last-saved time +
                      what to do next. In the dedicated workspace the shell footer
                      owns Save (via evalws:save) — so suppress this in-pane bar. ── */}
                  {!workspaceMode && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      marginTop: 14,
                      paddingTop: 12,
                      borderTop: '1px solid var(--rule)',
                    }}
                  >
                    <Btn3
                      intent="grn"
                      size="sm"
                      onClick={onSaveFloors}
                      disabled={savingFloors}
                    >
                      {savingFloors ? 'Saving…' : 'Save floor'}
                    </Btn3>
                    {floorsDirty ? (
                      <Tag kind="warn" title="You have edits not yet saved">
                        unsaved changes
                      </Tag>
                    ) : floorsSavedAt ? (
                      <Tag kind="val">
                        Saved · {sum.doorCount} door
                        {sum.doorCount === 1 ? '' : 's'}
                        {sum.gridReady ? ' · grid ready' : ''} ·{' '}
                        {floorsSavedAt.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Tag>
                    ) : (
                      <span
                        style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}
                      >
                        Not saved yet — press “Save floor”.
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 11.5,
                        color: 'var(--ink-mute)',
                        flexBasis: '100%',
                      }}
                    >
                      Next: switch to “Mark AUO”, place each fixture, ask the
                      engine, then Confirm the ones that belong · or open another
                      floor above.
                    </span>
                  </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}

        {/* WORKSPACE (v3.5): no in-panel add-floor / save row — the shell footer
            owns Save draft / Save evaluation (via the evalws:save bus). */}
        {!workspaceMode && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <Btn3 intent="ink" size="sm" onClick={addFloor}>
            + Add floor
          </Btn3>
          <Btn3
            intent="grn"
            size="sm"
            onClick={onSaveFloors}
            disabled={savingFloors}
          >
            {savingFloors ? 'Saving…' : 'Save all floors'}
          </Btn3>
          {floorsDirty && (
            <Tag kind="warn" title="You have edits not yet saved">
              unsaved changes
            </Tag>
          )}
        </div>
        )}
      </FloorsWrap>
      )} {/* end linkedPropertyIds ternary */}
    </>
  );
}
